import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { collegeJourneyService } from '../../lib/college-journey-service';
import ConversationalResults from './ConversationalResults';
import AssessmentProgressIndicator from './AssessmentProgressIndicator';
import AssessmentDataService from '../../services/AssessmentDataService';


// Debug toggle - set to true to enable detailed validation logging
const DEBUG_VALIDATION = true;
import { 
  Sparkles, 
  ArrowRight, 
  Brain, 
  Zap, 
  Target,
  Lightbulb,
  TrendingUp,
  Layers,
  ChevronLeft,
  CheckCircle,
  Send,
  Clock,
  MessageCircle,
  User,
  Bot,
  TestTube,
  Edit3
} from 'lucide-react';


// Enhanced Pattern Recognition System aligned with new thread structure
const patternRecognition = {
  // High Priority Threads (Weight 3) - Select First
  highPriority: {
    // 1. Edge case indicators - Unique knowledge and experiences
    edge_case_indicators: {
      keywords: ['most people dont', 'unusual', 'weird', 'random', 'specific', 'niche', 'specialized', 'know a lot about', 'expert in', 'uncommon', 'rare', 'held back', 'not allowed', 'too young', 'grade level', 'not ready', 'system', 'didn\'t know what to do'],
      weight: 3,
      maps_to: 'edge_case_uniqueness',
      description: 'Unique knowledge, specialized interests, system conflicts'
    },
    
    // 2. Values-driven choices - Principled decisions that cost something
    values_driven: {
      keywords: ['do it again', 'was right', 'had to', 'couldn\'t let', 'worth it', 'still believe', 'no regrets', 'principle', 'right thing', 'broke rule', 'got in trouble'],
      weight: 3,
      maps_to: 'values_social',
      description: 'Principled decisions that cost something'
    },
    
    // 3. Unique intersections - Unusual combinations of interests/skills
    unique_intersections: {
      keywords: ['combination', 'both', 'also', 'unexpected', 'unusual', 'different areas', 'connects', 'bridge', 'cross over', 'mix'],
      weight: 3,
      maps_to: 'problem_solving_innovation',
      description: 'Unusual combinations of interests/skills'
    },
    
    // Natural capabilities indicators - Enhanced for "lose track of time" question
    natural_capabilities: {
      keywords: ['taught myself', 'figured out', 'comes naturally', 'find easy', 'others struggle', 'my process', 'how I learn', 'lose track of time', 'hours', 'focused', 'absorbed', 'flow', 'zone', 'get into'],
      weight: 3,
      maps_to: 'natural_capabilities',
      description: 'Evidence of distinct learning patterns and natural abilities'
    },
    
    // Intellectual curiosity indicators - Enhanced for "learning discovery" question
    intellectual_curiosity: {
      keywords: ['didnt think id like', 'ended up loving', 'obsessed', 'passionate', 'discovered', 'unexpectedly', 'surprised myself', 'turned out', 'rabbit hole', 'research', 'curious', 'explore', 'fascinated'],
      weight: 3,
      maps_to: 'intellectual_curiosity',
      description: 'Evidence of intellectual curiosity and growth mindset'
    },
    
    // Innovation and system improvement - For "broken system" question  
    system_innovation: {
      keywords: ['better way', 'different approach', 'works fine but', 'could be improved', 'more efficient', 'easier way', 'make it better', 'fix', 'improve', 'redesign', 'automate', 'streamline', 'optimize'],
      weight: 3,
      maps_to: 'problem_solving_innovation',
      description: 'Evidence of systems thinking and innovation'
    },

    // Advice/helping patterns
    trusted_advisor: {
      keywords: ['advice', 'help', 'ask me', 'come to me', 'trust', 'listen', 'guidance', 'support'],
      weight: 3,
      maps_to: 'values_social',
      description: 'Natural helper and trusted perspective'
    }
  },
  
  // Secondary Threads (Weight 2) - Fill remaining slots
  secondary: {
    // 4. Quirky competencies - Random expertise, pattern recognition
    quirky_competencies: {
      keywords: ['memorize', 'memorized', 'know every', 'random', 'weird', 'useless', 'no reason', 'pointless', 'serves no purpose', 'notice', 'spot patterns', 'predict'],
      weight: 2,
      maps_to: 'intellectual_curiosity',
      description: 'Random expertise, pattern recognition'
    },
    
    // 5. Growth through challenge - Learning from failure, persistence  
    growth_challenge: {
      keywords: ['frustrated', 'failed', 'gave up', 'kept trying', 'persisted', 'learned from', 'mistake', 'setback', 'difficult', 'challenge', 'struggle'],
      weight: 2,
      maps_to: 'intellectual_curiosity',
      description: 'Learning from failure, persistence'
    },
    
    // Sleep/rest patterns (could indicate overwhelm or specific routines)
    rest_recharge: {
      keywords: ['sleep', 'tired', 'exhausted', 'rest', 'recharge', 'overwhelmed', 'busy', 'stressed'],
      weight: 2,
      maps_to: 'values_social',
      description: 'Self-care and balance indicators'
    },
    
    // Group dynamics patterns
    group_dynamics: {
      keywords: ['hope for the best', 'do my part', 'team', 'group', 'work together', 'collaborate', 'others', 'responsibility'],
      weight: 2,
      maps_to: 'values_social',
      description: 'How they approach group situations'
    }
  },
  
  // Traditional baseline patterns
  traditional: {
    builder: ['build', 'create', 'make', 'design', 'construct', 'develop', 'invent'],
    helper: ['help', 'support', 'assist', 'care', 'serve', 'guide', 'teach'],
    analyzer: ['understand', 'analyze', 'solve', 'figure', 'research', 'investigate', 'study'],
    leader: ['organize', 'lead', 'manage', 'coordinate', 'direct', 'inspire', 'motivate'],
    connector: ['people', 'community', 'together', 'bridge', 'unite', 'collaborate', 'team'],
    advocate: ['justice', 'fair', 'right', 'equal', 'voice', 'stand', 'fight']
  },
  
  // Red Flag Phrases for Deeper Exploration
  redFlags: {
    minimization: {
      keywords: ['just', 'only', 'simple', 'nothing special', 'probably normal', 'not that big a deal'],
      description: 'Downplaying potentially unique experiences'
    },
    system_adaptation: {
      keywords: ['learned to fit in', 'stopped asking', 'gave up trying', 'figured out how to', 'had to adapt'],
      description: 'Evidence of accommodating systems rather than being accommodated'
    },
    external_validation: {
      keywords: ['everyone says', 'people think', 'supposed to', 'should', 'have to'],
      description: 'May be suppressing authentic perspectives'
    }
  }
};


const ConversationalAssessment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, switchUser } = useFamilyContext();
  const { masterStrategy } = useCollegeContext();
  
  // Check if we're in revision mode based on navigation state
  const revisionMode = location.state?.revisionMode || false;
  const originalAssessmentId = location.state?.assessmentId || null;
  
  // Core state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [conversationHistory, setConversationHistory] = useState([]);
  const [patternScores, setPatternScores] = useState({
    highPriority: {},
    secondary: {},
    traditional: {},
    redFlags: {}
  });
  const [isThinking, setIsThinking] = useState(false);
  const [processingError, setProcessingError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState({
    currentStep: 0,
    totalSteps: 5,
    stepName: '',
    startTime: null
  });
  const [showResults, setShowResults] = useState(false);
  const [currentInput, setCurrentInput] = useState('');
  
  // Testing mode state
  const [isTestingMode, setIsTestingMode] = useState(false);
  const [mockResponseSet, setMockResponseSet] = useState(null);
  const [showSaveResponsesModal, setShowSaveResponsesModal] = useState(false);
  const [responseSetName, setResponseSetName] = useState('');
  const [responseSetDescription, setResponseSetDescription] = useState('');
  
  // Revision mode state
  const [revisionModeLoading, setRevisionModeLoading] = useState(revisionMode);
  const [previousResponses, setPreviousResponses] = useState(null);
  
  // Ref for textarea focus management
  const textareaRef = useRef(null);
  const conversationContainerRef = useRef(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingText, setEditingText] = useState('');
  
  // Assessment flow state - 4 phases
  const [assessmentPhase, setAssessmentPhase] = useState('loading'); // loading, ready, opening, followup, synthesis, results
  const [allQuestions, setAllQuestions] = useState([]);
  const [studentProfile, setStudentProfile] = useState(null);
  const [parentContext, setParentContext] = useState(null);
  
  // Enhanced 6-phase assessment state
  const [strategicContext, setStrategicContext] = useState('');
  const [initialQuestions, setInitialQuestions] = useState([]);
  const [dynamicQuestionGuidance, setDynamicQuestionGuidance] = useState('');
  
  // Report generation settings - always generate full strategic report (hard-coded)
  
  // Fallback questions if enhanced data isn't available
  const fallbackInitialQuestions = [
    {
      id: 'q1',
      question: 'What genuinely excites you about learning, beyond just getting good grades?',
      type: 'text',
      placeholder: 'Think about subjects, topics, or ways of learning that make you lose track of time...'
    },
    {
      id: 'q2', 
      question: 'Describe a challenge you faced recently and how you approached solving it.',
      type: 'text',
      placeholder: 'This could be academic, personal, or in any activity you care about...'
    },
    {
      id: 'q3',
      question: 'What do you do when you have free time that no one is telling you to do?',
      type: 'text', 
      placeholder: 'Think about activities you choose purely because you enjoy them...'
    },
    {
      id: 'q4',
      question: 'What kind of impact do you want to have on the world, even if you\'re not sure how to get there yet?',
      type: 'text',
      placeholder: 'This could be big or small, immediate or long-term...'
    },
    {
      id: 'q5',
      question: 'Tell me about a time when you had to explain something complex to someone else. How did you approach it?',
      type: 'text',
      placeholder: 'Think about how you break down ideas for others...'
    },
    {
      id: 'q6',
      question: 'What\'s something you believe that might be different from what most people your age think?',
      type: 'text',
      placeholder: 'This could be about school, life, society, or anything important to you...'
    },
    {
      id: 'q7',
      question: 'Describe your ideal learning environment. What helps you think and create your best work?',
      type: 'text',
      placeholder: 'Consider both physical environment and the people around you...'
    },
    {
      id: 'q8',
      question: 'What\'s a skill or ability you have that others might not expect based on first impressions?',
      type: 'text',
      placeholder: 'Think about hidden strengths or unexpected combinations of interests...'
    }
  ];
  
  // Debug validation dashboard state (removed unused variables)
  const [synthesisInstructions, setSynthesisInstructions] = useState('');
  const [studentSpecificConsiderations, setStudentSpecificConsiderations] = useState('');
  const [_personalityProfile, setPersonalityProfile] = useState({});
  const [formattingInstructions, setFormattingInstructions] = useState('');
  const [sectionTitles, setSectionTitles] = useState({});
  const [tonePreferences, setTonePreferences] = useState({});
  const [metaphorStyle, setMetaphorStyle] = useState('');
  // Processing steps are now handled by AssessmentProgressIndicator
  

  useEffect(() => {
    // Only load foundation data on initial mount or when user actually changes
    // This prevents unnecessary reloads that interrupt the assessment
    if (currentUser && assessmentPhase === 'loading') {
      console.log('🔄 Loading foundation data for assessment initialization');
      
      // Check for testing mode by comparing user IDs
      const savedStudentId = localStorage.getItem('college-selected-student-id');
      const currentUserAuthId = currentUser?.auth_user_id || currentUser?.authUserId;
      const testingMode = savedStudentId && savedStudentId !== currentUserAuthId;
      setIsTestingMode(testingMode);
      
      console.log('🎯 ConversationalAssessment: Testing mode detection:', {
        savedStudentId,
        currentUserAuthId,
        testingMode
      });
      
      if (testingMode) {
        const mockResponseData = localStorage.getItem('mock-response-set');
        if (mockResponseData) {
          try {
            const { responseSet, type } = JSON.parse(mockResponseData);
            setMockResponseSet({ responseSet, type });
            console.log('📝 Loaded mock response set:', responseSet.name, type);
            
            // Pre-fill responses based on the type and set starting mode
            if (responseSet.responses) {
              const responsesToUse = {};
              
              if (type === '1-8') {
                // For 1-8: Pre-fill questions 1-8 and prepare to start at question 9
                console.log('🎯 Testing mode: Using responses 1-8, will start at question 9');
                
                // Pre-fill initial questions (1-8) from initial questions
                initialQuestions.slice(0, 8).forEach((q) => {
                  if (responseSet.responses[q.id]) {
                    responsesToUse[q.id] = responseSet.responses[q.id];
                  }
                });
                
                // Pre-fill first 3 followup responses (6-8) if they exist
                if (responseSet.responses.followup) {
                  const followupKeys = Object.keys(responseSet.responses.followup).slice(0, 3);
                  followupKeys.forEach(key => {
                    responsesToUse[key] = responseSet.responses.followup[key];
                  });
                }
                
                setResponses(responsesToUse);
                console.log('✅ Pre-filled responses 1-8:', Object.keys(responsesToUse).length);
                
              } else if (type === '1-15') {
                // For 1-15: Pre-fill all responses and prepare to skip to insights
                console.log('🎭 Testing mode: Using responses 1-15, will skip to insights generation');
                console.log('🔍 Available responseSet.responses:', Object.keys(responseSet.responses || {}));
                console.log('🔍 Available responseSet.responses.followup:', Object.keys(responseSet.responses?.followup || {}));
                console.log('🔍 initialQuestions available:', initialQuestions.length);
                
                // Pre-fill initial questions (1-8) from initial questions
                if (initialQuestions.length > 0) {
                  initialQuestions.slice(0, 8).forEach((q) => {
                    if (responseSet.responses[q.id]) {
                      responsesToUse[q.id] = responseSet.responses[q.id];
                      console.log('✅ Pre-filled initial response:', q.id);
                    }
                  });
                }
                
                // Pre-fill all followup responses (9-15)
                if (responseSet.responses.followup) {
                  Object.assign(responsesToUse, responseSet.responses.followup);
                  console.log('✅ Pre-filled followup responses:', Object.keys(responseSet.responses.followup));
                }
                
                setResponses(responsesToUse);
                console.log('✅ Pre-filled all responses 1-15:', Object.keys(responsesToUse).length);
                console.log('✅ responsesToUse keys:', Object.keys(responsesToUse));
              }
            }
          } catch (error) {
            console.error('Error loading mock response set:', error);
          }
        }
      }
      
      loadFoundationData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, assessmentPhase]); // Only depend on user ID, not the full user object

  // Load previous responses for revision mode
  useEffect(() => {
    const loadPreviousResponses = async () => {
      if (!revisionMode || !currentUser?.id || !originalAssessmentId) {
        setRevisionModeLoading(false);
        return;
      }

      try {
        console.log('🔄 Loading previous responses for revision mode:', originalAssessmentId);
        const assessmentData = await AssessmentDataService.getAssessmentById(originalAssessmentId);
        
        if (assessmentData && assessmentData.responses) {
          const preparedResponses = AssessmentDataService.prepareResponsesForRevision(assessmentData.responses);
          setPreviousResponses(preparedResponses);
          setResponses(preparedResponses); // Pre-populate the form
          console.log('✅ Previous responses loaded and pre-populated');
        } else {
          console.warn('⚠️ No responses found for assessment:', originalAssessmentId);
        }
      } catch (error) {
        console.error('❌ Error loading previous responses:', error);
      } finally {
        setRevisionModeLoading(false);
      }
    };

    loadPreviousResponses();
  }, [revisionMode, currentUser?.id, originalAssessmentId]);

  useEffect(() => {
    // Re-check requirements when profile data or initial questions change
    if (assessmentPhase === 'loading') {
      console.log('🔄 useEffect triggering checkDataRequirements, initialQuestions length:', initialQuestions.length);
      checkDataRequirements();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentProfile, parentContext, initialQuestions, assessmentPhase]);

  // Safety check to ensure proper initial state
  useEffect(() => {
    if (assessmentPhase === 'opening' && allQuestions.length > 0 && currentQuestionIndex >= allQuestions.length) {
      console.warn('⚠️ Fixing out-of-bounds currentQuestionIndex:', currentQuestionIndex, 'resetting to 0');
      setCurrentQuestionIndex(0);
    }
  }, [assessmentPhase, allQuestions.length, currentQuestionIndex]);
  
  // Handle testing modes AFTER assessment is ready
  useEffect(() => {
    if (assessmentPhase === 'ready' && mockResponseSet) {
      console.log('🎯 Testing mode detected in ready phase:', mockResponseSet.type);
      
      if (mockResponseSet.type === '1-8') {
        // For 1-8 mode: Pre-fill responses 1-8 and prepare to start at question 9
        console.log('🎯 Setting up 1-8 mode: Will auto-advance to question 9 after starting');
        
        // The responses should already be pre-filled from the loading phase
        // Just log the state for debugging
        console.log('📝 Pre-filled responses count:', Object.keys(responses).length);
        console.log('📝 Response keys:', Object.keys(responses));
        
      } else if (mockResponseSet.type === '1-15') {
        // For 1-15 mode: Pre-fill all responses and prepare to skip directly to insights
        console.log('🎯 Setting up 1-15 mode: Will auto-generate insights after starting');
        
        // The responses should already be pre-filled from the loading phase
        console.log('📝 Pre-filled responses count:', Object.keys(responses).length);
        console.log('📝 All response keys:', Object.keys(responses));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentPhase, mockResponseSet]);
  
  // Pre-fill current input when question changes and there's a pre-filled response
  useEffect(() => {
    const currentQuestion = getCurrentQuestion();
    if (currentQuestion && responses[currentQuestion.id] && !currentInput) {
      // Only pre-fill if we haven't typed anything yet
      setCurrentInput(responses[currentQuestion.id]);
      console.log('🔄 Pre-filled response for question:', currentQuestion.id);
    } else if (currentQuestion && !responses[currentQuestion.id]) {
      // Clear input for new questions without pre-filled responses
      setCurrentInput('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex, assessmentPhase]);
  
  // Debug: Monitor allQuestions changes
  useEffect(() => {
    console.log('🔍 DEBUG: allQuestions changed:', {
      length: allQuestions.length,
      firstQuestion: allQuestions[0]?.question?.substring(0, 50) + '...' || 'none',
      hasEnhanced: allQuestions[0]?.question?.includes('online gam') || false
    });
  }, [allQuestions]);
  
  // Auto-focus on textarea when question changes and autoscroll
  useEffect(() => {
    const timer = setTimeout(() => {
      if (textareaRef.current && !isThinking) {
        textareaRef.current.focus();
        console.log('🔍 Auto-focusing on question', currentQuestionIndex + 1);
        
        // Autoscroll to the current question (smooth scroll to bottom)
        if (conversationContainerRef.current) {
          conversationContainerRef.current.scrollTo({
            top: conversationContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }
    }, 150); // Slightly longer delay to ensure DOM is updated after thinking state
    
    return () => clearTimeout(timer);
  }, [currentQuestionIndex, assessmentPhase, isThinking]);

  const handleSwitchBackToReal = async () => {
    try {
      const realUser = localStorage.getItem('real-user-context');
      if (realUser) {
        const parsedRealUser = JSON.parse(realUser);
        console.log('🔄 Restoring real user context:', parsedRealUser);
        
        // Switch user and wait for state to update
        switchUser(parsedRealUser);
        
        // Clear the stored real user context
        localStorage.removeItem('real-user-context');
        
        // Return the parsed user for immediate use
        return parsedRealUser;
      }
      // Don't clear testing mode here - wait until after save modal
      // localStorage.removeItem('testing-mode');
      localStorage.removeItem('testing-profile-id');
      localStorage.removeItem('mock-college-preferences');
      
      return null;
    } catch (error) {
      console.error('Error switching back to real user:', error);
      return null;
    }
  };

  const handleBackNavigation = async () => {
    const isInTestingMode = localStorage.getItem('testing-mode') === 'true';
    
    if (isInTestingMode) {
      // Switch back to real user first
      await handleSwitchBackToReal();
      
      // Use window.location for a clean navigation that ensures context is fresh
      // This forces a full page reload which will pick up the restored user from localStorage
      window.location.href = '/college/parent-testing';
    } else {
      // Normal navigation to college journey
      navigate('/college');
    }
  };

  const loadFoundationData = async () => {
    if (!currentUser) {
      console.log('❌ No currentUser available for loading foundation data');
      return;
    }
    
    // Check if we're in testing mode by comparing user IDs
    const savedStudentId = localStorage.getItem('college-selected-student-id');
    const currentUserAuthId = currentUser.auth_user_id || currentUser.authUserId;
    const isTestingMode = savedStudentId && savedStudentId !== currentUserAuthId;
    
    console.log('🎯 ASSESSMENT: Testing mode detection:', {
      savedStudentId,
      currentUserAuthId,
      isTestingMode
    });
    
    // Get the correct user IDs for different data sources
    // Assessment responses table uses family_members.id as foreign key
    // Student academic profiles table uses auth_user_id as foreign key
    const familyMemberId = currentUser.id; // For assessment responses
    
    let authUserId;
    if (isTestingMode) {
      // Testing mode: Use the selected student's auth_user_id from localStorage
      authUserId = savedStudentId;
      console.log('🎯 ASSESSMENT: Using auth_user_id from localStorage (testing):', authUserId);
    } else {
      // Normal mode: Use the current user's auth_user_id
      authUserId = currentUserAuthId;
      console.log('🎯 ASSESSMENT: Using current user auth_user_id (normal):', authUserId);
    }
    
    console.log('🎯 ASSESSMENT: Loading foundation data for user:', currentUser.name);
    console.log('🎯 ASSESSMENT: User ID resolution:', {
      'familyMemberId (for responses)': familyMemberId,
      'authUserId (for profiles)': authUserId,
      'currentUser object': currentUser,
      'isTestingMode': isTestingMode
    });
    
    try {
      // Use simplified approach with the centralized service
      console.log('🔍 Loading academic profile using service...');
      
      const profileData = await collegeJourneyService.getAcademicProfile(authUserId);
      console.log('🔍 Academic profile loaded:', profileData);
      
      // CRITICAL DEBUG: Show what we got from the database
      console.log('🚨 CRITICAL DATABASE DEBUG:', {
        authUserId: authUserId,
        profileDataKeys: profileData ? Object.keys(profileData) : 'NULL',
        synthesis_instructions: {
          exists: !!profileData?.synthesis_instructions,
          length: profileData?.synthesis_instructions?.length || 0,
          preview: profileData?.synthesis_instructions?.substring(0, 100) || 'EMPTY'
        },
        claude_synthesis_instructions: {
          exists: !!profileData?.claude_synthesis_instructions,
          length: profileData?.claude_synthesis_instructions?.length || 0
        },
        formatting_instructions: {
          exists: !!profileData?.formatting_instructions,
          length: profileData?.formatting_instructions?.length || 0,
          preview: profileData?.formatting_instructions?.substring(0, 100) || 'EMPTY'
        },
        section_titles: {
          exists: !!profileData?.section_titles,
          keys: profileData?.section_titles ? Object.keys(profileData.section_titles) : []
        },
        assessment_phase: profileData?.assessment_phase || 'NOT SET'
      });
      
      // Also check for saved assessment responses (uses family member ID)
      const savedResponses = await collegeJourneyService.getSavedAssessmentResponses(familyMemberId);
      console.log('🔍 Saved assessment responses:', savedResponses);
      
      if (profileData && Object.keys(profileData).length > 0) {
        // Transform database data to match expected format
        const studentProfile = {
          academicInterests: profileData.academic_interests || [],
          careerGoals: profileData.career_goals || '',
          learningStyle: profileData.learning_style || '',
          academicStrengths: profileData.academic_strengths || [],
          uniqueQualities: profileData.unique_qualities || '',
          studentSelfAssessment: profileData.student_self_assessment || '',
          // Add college preferences in the format the API expects
          collegePreferences: {
            intendedMajors: profileData.intended_major ? [profileData.intended_major] : [],
            keyCriteria: profileData.career_goals || '',
            studentUniqueInsight: profileData.unique_qualities || '',
            // Add fallback from intended_career field
            intendedCareer: profileData.intended_career || ''
          }
        };
        
        setStudentProfile(studentProfile);
        console.log('Loaded student profile from database:', studentProfile);
        
        // Transform parent observations from database fields
        const parentObservations = {
          easySubject: profileData.parent_observation_easy_subject || '',
          growth: profileData.parent_observation_growth || '',
          poorFit: profileData.parent_observation_poor_fit || '',
          surprises: profileData.parent_observation_surprises || '',
          ageLimit: profileData.parent_observation_age_limit || '',
          rulesQuestioned: profileData.parent_observation_rules_questioned || '',
          wrongLevel: profileData.parent_observation_wrong_level || '',
          selfTaught: profileData.parent_observation_self_taught || '',
          obsessions: profileData.parent_observation_obsessions || '',
          unusualInterests: profileData.parent_observation_unusual_interests || '',
          hiddenQualities: profileData.parent_observation_hidden_qualities || ''
        };
        
        // Only set parent context if we have meaningful data
        const hasParentData = Object.values(parentObservations).some(value => value && value.trim());
        if (hasParentData) {
          setParentContext(parentObservations);
          console.log('Loaded parent observations from database:', parentObservations);
        }

        // Load college preferences
        let collegePreferencesData = null;
        
        // First check if we're in testing mode
        if (isTestingMode) {
          const mockPreferencesData = localStorage.getItem('mock-college-preferences');
          if (mockPreferencesData) {
            try {
              collegePreferencesData = JSON.parse(mockPreferencesData);
              console.log('🎯 COLLEGE PREFERENCES: Loaded from localStorage (testing mode):', collegePreferencesData);
            } catch (e) {
              console.error('Error parsing mock college preferences:', e);
            }
          }
        }
        
        // If not testing mode or no mock preferences, try to load from database
        if (!collegePreferencesData) {
          // Check college_journey_progress table
          const { data: journeyData, error: _ } = await supabase
            .from('college_journey_progress')
            .select('college_preferences')
            .eq('user_id', authUserId)
            .single();
            
          if (journeyData?.college_preferences) {
            collegePreferencesData = journeyData.college_preferences;
            console.log('🎯 COLLEGE PREFERENCES: Loaded from college_journey_progress:', collegePreferencesData);
          } else {
            console.log('🎯 COLLEGE PREFERENCES: No preferences found in database');
          }
        }

        // Add college preferences to student profile for API usage
        if (collegePreferencesData) {
          studentProfile.collegePreferences = collegePreferencesData;
          console.log('🎯 COLLEGE PREFERENCES: Added to student profile for API:', collegePreferencesData);
        }

        // Check for 6-phase strategic assessment data
        const hasStrategicContext = profileData.strategic_context_summary && profileData.strategic_context_summary.trim();
        const hasInitialQuestions = profileData.initial_question_set && profileData.initial_question_set.length > 0;
        const hasSynthesisInstructions = profileData.synthesis_instructions && profileData.synthesis_instructions.trim();
        
        console.log('🔍 DEBUG: Phase completion status:', {
          phase2_strategic_context: hasStrategicContext,
          phase3_initial_questions: hasInitialQuestions, 
          phase5_synthesis_instructions: hasSynthesisInstructions,
          assessment_phase: profileData.assessment_phase
        });
        
        // Set enhanced mode immediately if Phase 5 data exists (definitive sign of 6-phase completion)
        // Or if we have Phase 2/3 data (fallback for partial completion)
        if (hasSynthesisInstructions || hasStrategicContext || hasInitialQuestions) {
          console.log('✅ Enhanced assessment data found - using 6-phase mode');
          
          console.log('🔍 DEBUG: Enhanced assessment data from DB:', {
            strategicContextSummary: {
              exists: !!profileData.strategic_context_summary,
              length: profileData.strategic_context_summary?.length || 0
            },
            synthesisInstructions: {
              exists: !!profileData.synthesis_instructions,
              length: profileData.synthesis_instructions?.length || 0,
              preview: profileData.synthesis_instructions?.substring(0, 200) || 'EMPTY'
            },
            studentSpecificConsiderations: {
              exists: !!profileData.student_specific_considerations,
              length: profileData.student_specific_considerations?.length || 0
            },
            personalityProfile: {
              exists: !!profileData.personality_profile,
              type: typeof profileData.personality_profile
            },
            initialQuestionSet: {
              exists: !!profileData.initial_question_set,
              length: profileData.initial_question_set?.length || 0
            }
          });
          
          // Set all the enhanced assessment data
          setStrategicContext(profileData.strategic_context_summary || '');
          setInitialQuestions(profileData.initial_question_set || []);
          setDynamicQuestionGuidance(profileData.dynamic_question_guidance || '');
          setSynthesisInstructions(profileData.synthesis_instructions || '');
          setStudentSpecificConsiderations(profileData.student_specific_considerations || '');
          
          // CRITICAL DEBUG: Log what we're actually loading
          console.log('🚨 CRITICAL: Loading synthesis instructions:', {
            exists: !!profileData.synthesis_instructions,
            length: profileData.synthesis_instructions?.length || 0,
            preview: profileData.synthesis_instructions?.substring(0, 200) || 'EMPTY',
            claudeSynthesisInstructions: {
              exists: !!profileData.claude_synthesis_instructions,
              length: profileData.claude_synthesis_instructions?.length || 0
            }
          });
          
          // Load formatting preferences from Phase 6
          setFormattingInstructions(profileData.formatting_instructions || '');
          setSectionTitles(profileData.section_titles || {});
          setTonePreferences(profileData.tone_preferences || {});
          setMetaphorStyle(profileData.metaphor_style || '');
          
          // CRITICAL DEBUG: Log formatting instructions
          console.log('🚨 CRITICAL: Loading formatting instructions:', {
            exists: !!profileData.formatting_instructions,
            length: profileData.formatting_instructions?.length || 0,
            preview: profileData.formatting_instructions?.substring(0, 200) || 'EMPTY',
            sectionTitles: Object.keys(profileData.section_titles || {}),
            metaphorStyle: profileData.metaphor_style || 'NONE'
          });
          
          // Warning if Phase 5 is incomplete
          if (!hasSynthesisInstructions) {
            console.warn('⚠️ WARNING: Enhanced assessment mode enabled but Phase 5 (Synthesis Instructions) appears incomplete!');
            console.warn('⚠️ This may result in generic insights instead of personalized content.');
            console.warn('⚠️ Consider completing Phase 5 first for optimal results.');
          }
          
          // Load personality profile
          if (profileData.personality_profile) {
            try {
              const personality = typeof profileData.personality_profile === 'string' 
                ? JSON.parse(profileData.personality_profile) 
                : profileData.personality_profile;
              setPersonalityProfile(personality);
            } catch {
              console.log('⚠️ Could not parse personality profile from database');
            }
          }
          
          // Set up initial questions for enhanced assessment
          console.log('🔍 DEBUG: Processing initial_question_set:', {
            raw: profileData.initial_question_set,
            type: typeof profileData.initial_question_set,
            isArray: Array.isArray(profileData.initial_question_set),
            length: profileData.initial_question_set?.length
          });
          
          let parsedQuestions = profileData.initial_question_set;
          
          // Parse JSON string if needed
          if (typeof profileData.initial_question_set === 'string') {
            try {
              parsedQuestions = JSON.parse(profileData.initial_question_set);
              console.log('🔍 DEBUG: Parsed JSON questions:', parsedQuestions);
            } catch (e) {
              console.error('❌ Failed to parse initial_question_set JSON:', e);
              parsedQuestions = null;
            }
          }
          
          if (parsedQuestions && Array.isArray(parsedQuestions) && parsedQuestions.length > 0) {
            // Ensure each question has the required type property
            const questionsWithType = parsedQuestions.map(q => ({
              ...q,
              type: q.type || 'text', // Add type property if missing
              placeholder: q.placeholder || 'Share your thoughts...'
            }));
            setAllQuestions(questionsWithType);
            setInitialQuestions(questionsWithType); // CRITICAL: Set initialQuestions to enhanced questions, not fallback
            console.log('✅ Using parent-crafted questions:', questionsWithType);
            console.log('✅ Sample question text:', questionsWithType[0]?.question);
            console.log('✅ Set initialQuestions to enhanced questions, length:', questionsWithType.length);
            
            // Debug: Check if allQuestions gets overridden later
            setTimeout(() => {
              console.log('🔍 DEBUG: allQuestions after 100ms (this may be stale due to closure)');
            }, 100);
          } else {
            // Use fallback questions if no enhanced data
            console.log('⚠️ No enhanced questions found, using fallback questions');
            setInitialQuestions(fallbackInitialQuestions);
            setAllQuestions([...fallbackInitialQuestions]);
          }
        } else {
          // No enhanced data found, use fallback questions
          console.log('⚠️ No enhanced assessment data, using fallback questions');
          setInitialQuestions([...fallbackInitialQuestions]);
          setAllQuestions([...fallbackInitialQuestions]);
        }
        
        // Load saved assessment responses if available
        if (savedResponses && savedResponses.responses && savedResponses.responses.length > 0) {
          console.log('✅ Loading saved assessment responses:', savedResponses);
          setResponses(savedResponses.responses);
          setPatternScores(savedResponses.pattern_scores || {});
          setConversationHistory(savedResponses.conversation_history || []);
          // Set to results phase if we have insights
          if (savedResponses.insights) {
            setAssessmentPhase('results');
            console.log('✅ Restored assessment to results phase with saved data');
          }
        }
        
        // Don't call checkDataRequirements immediately - let useEffect handle it after state updates
        return; // Exit early since we found database data
      } else {
        console.log('No student profile found in database - student may need to complete onboarding');
      }
      
      // Try to get data from master strategy if database didn't have it
      if (!profileData && masterStrategy) {
        console.log('🔍 Checking master strategy for profile data:', masterStrategy);
        if (masterStrategy.student_profile) {
          setStudentProfile(masterStrategy.student_profile);
          console.log('✅ Loaded student profile from master strategy');
        }
        if (masterStrategy.parent_observations) {
          setParentContext(masterStrategy.parent_observations);
          console.log('✅ Loaded parent context from master strategy');
        }
      }
      
      // Fallback to localStorage if no database data found (for backwards compatibility)
      if (!profileData && !masterStrategy?.student_profile) {
        console.log('🔍 Falling back to localStorage for student profile data');
        
        // Check various localStorage keys
        const onboardingData = localStorage.getItem('student-onboarding-data');
        const profileData = localStorage.getItem('student-profile-data');
        const academicProfile = localStorage.getItem('academic-profile-data');
        
        console.log('🔍 localStorage contents:', {
          'student-onboarding-data': !!onboardingData,
          'student-profile-data': !!profileData,
          'academic-profile-data': !!academicProfile
        });
        
        if (onboardingData) {
          const data = JSON.parse(onboardingData);
          setStudentProfile(data);
          console.log('✅ Loaded student profile from localStorage onboarding:', data);
        } else if (profileData) {
          const data = JSON.parse(profileData);
          setStudentProfile(data);
          console.log('✅ Loaded student profile from localStorage profile:', data);
        } else if (academicProfile) {
          const data = JSON.parse(academicProfile);
          setStudentProfile(data);
          console.log('✅ Loaded student profile from localStorage academic:', data);
        }
        
        const parentData = localStorage.getItem('parent-observations');
        const parentContext = localStorage.getItem('parent-context');
        
        if (parentData) {
          setParentContext(JSON.parse(parentData));
          console.log('✅ Loaded parent context from localStorage observations:', JSON.parse(parentData));
        } else if (parentContext) {
          setParentContext(JSON.parse(parentContext));
          console.log('✅ Loaded parent context from localStorage context:', JSON.parse(parentContext));
        }
      }
      
    } catch (error) {
      console.error('Error loading foundation data:', error);
      // Fallback to localStorage on any database error
      const onboardingData = localStorage.getItem('student-onboarding-data');
      if (onboardingData) {
        const data = JSON.parse(onboardingData);
        setStudentProfile(data);
        console.log('Fallback: Loaded student profile from localStorage');
      }
      
      const parentData = localStorage.getItem('parent-observations');
      if (parentData) {
        setParentContext(JSON.parse(parentData));
        console.log('Fallback: Loaded parent context from localStorage');
      }
    }
    
    // Check if we have the required foundation data (pass loaded data directly)
    checkDataRequirements();
  };

  const checkDataRequirements = (loadedStudentProfile = null, loadedParentContext = null) => {
    // Use passed-in data if available, otherwise fall back to state
    const profileToCheck = loadedStudentProfile || studentProfile;
    const parentContextToCheck = loadedParentContext || parentContext;
    
    // Check if we have at least basic student profile data
    const hasBasicProfile = profileToCheck && (
      profileToCheck.academicInterests?.length > 0 ||
      profileToCheck.careerGoals ||
      profileToCheck.learningStyle ||
      profileToCheck.uniqueQualities ||
      profileToCheck.name ||
      Object.keys(profileToCheck).length > 0
    );
    
    // Also check localStorage for any profile data as fallback
    const hasLocalStorageData = 
      localStorage.getItem('student-onboarding-data') ||
      localStorage.getItem('student-profile-data') ||
      localStorage.getItem('academic-profile-data');
    
    console.log('📊 Data requirements check:', {
      hasStudentProfile: !!profileToCheck,
      hasBasicProfile,
      hasLocalStorageData: !!hasLocalStorageData,
      hasParentContext: !!parentContextToCheck,
      assessmentPhase,
      studentProfileKeys: profileToCheck ? Object.keys(profileToCheck) : [],
      loadedDataUsed: !!loadedStudentProfile
    });
    
    // Very liberal data requirements - proceed if we have ANY student information
    const canProceed = hasBasicProfile || hasLocalStorageData || 
                      (currentUser && (currentUser.name || currentUser.displayName));
    
    console.log('📊 Final decision:', {
      hasBasicProfile,
      hasLocalStorageData: !!hasLocalStorageData,
      hasCurrentUser: !!currentUser,
      currentUserName: currentUser?.name || currentUser?.displayName,
      canProceed
    });
    
    if (canProceed) {
      setAssessmentPhase('ready');
      console.log('✅ Sufficient data found - assessment ready to begin');
      
      // Ensure opening questions are properly initialized
      if (!allQuestions || allQuestions.length === 0) {
        console.warn('⚠️ Re-initializing opening questions');
        const questionsToUse = initialQuestions.length > 0 ? initialQuestions : fallbackInitialQuestions;
        console.log('🔍 DEBUG: checkDataRequirements questionsToUse:', {
          source: initialQuestions.length > 0 ? 'initialQuestions (enhanced)' : 'fallbackInitialQuestions',
          length: questionsToUse.length,
          firstQuestion: questionsToUse[0]?.question?.substring(0, 50) + '...'
        });
        setAllQuestions([...questionsToUse]);
        if (initialQuestions.length === 0) {
          setInitialQuestions(fallbackInitialQuestions);
        }
      }
      
      
      // Don't create minimal profile if we have loaded data
      if (!profileToCheck && currentUser) {
        const minimalProfile = {
          name: currentUser.displayName || currentUser.name || 'Student',
          academicInterests: ['General'],
        };
        setStudentProfile(minimalProfile);
        console.log('✅ Created minimal profile for user:', minimalProfile);
      }
    } else {
      setAssessmentPhase('missing_data');
      console.log('❌ No student data found - need some basic information to proceed');
    }
  };

  const getCurrentQuestion = () => {
    // Safety check to ensure we have questions and valid index
    if (!allQuestions || allQuestions.length === 0) {
      console.warn('⚠️ No questions available in allQuestions array');
      return null;
    }
    
    if (currentQuestionIndex < 0 || currentQuestionIndex >= allQuestions.length) {
      console.warn('⚠️ currentQuestionIndex out of bounds:', currentQuestionIndex, 'Array length:', allQuestions.length);
      return null;
    }
    
    return allQuestions[currentQuestionIndex];
  };

  const analyzeResponsePatterns = (response) => {
    const text = response.toLowerCase();
    const newScores = { ...patternScores };
    const detected = [];
    
    // Check high priority patterns (Weight 3)
    Object.entries(patternRecognition.highPriority).forEach(([pattern, config]) => {
      const matchedKeywords = config.keywords.filter(keyword => text.includes(keyword));
      const matchCount = matchedKeywords.length;
      if (matchCount > 0) {
        newScores.highPriority = newScores.highPriority || {};
        newScores.highPriority[pattern] = (newScores.highPriority[pattern] || 0) + (matchCount * config.weight);
        detected.push(`🎯 High Priority: ${pattern} (${matchCount} matches: ${matchedKeywords.join(', ')}) → ${config.maps_to}`);
      }
    });
    
    // Check secondary patterns (Weight 2)
    Object.entries(patternRecognition.secondary).forEach(([pattern, config]) => {
      const matchedKeywords = config.keywords.filter(keyword => text.includes(keyword));
      const matchCount = matchedKeywords.length;
      if (matchCount > 0) {
        newScores.secondary = newScores.secondary || {};
        newScores.secondary[pattern] = (newScores.secondary[pattern] || 0) + (matchCount * config.weight);
        detected.push(`⭐ Secondary: ${pattern} (${matchCount} matches: ${matchedKeywords.join(', ')}) → ${config.maps_to}`);
      }
    });
    
    // Check traditional patterns
    Object.entries(patternRecognition.traditional).forEach(([pattern, keywords]) => {
      const matchedKeywords = keywords.filter(keyword => text.includes(keyword));
      const matchCount = matchedKeywords.length;
      if (matchCount > 0) {
        newScores.traditional[pattern] = (newScores.traditional[pattern] || 0) + matchCount;
        detected.push(`💼 Traditional: ${pattern} (${matchCount} matches: ${matchedKeywords.join(', ')})`);
      }
    });
    
    // Check red flags
    Object.entries(patternRecognition.redFlags).forEach(([pattern, config]) => {
      const matchedKeywords = config.keywords.filter(keyword => text.includes(keyword));
      const matchCount = matchedKeywords.length;
      if (matchCount > 0) {
        newScores.redFlags[pattern] = (newScores.redFlags[pattern] || 0) + matchCount;
        detected.push(`🚩 Red Flag: ${pattern} (${matchCount} matches: ${matchedKeywords.join(', ')})`);
      }
    });
    
    if (detected.length > 0) {
      console.log(`🔍 Pattern Analysis for: "${response.substring(0, 50)}..."`, detected);
    }
    
    setPatternScores(newScores);
    return newScores;
  };






  const handleResponse = async (response) => {
    const currentQuestion = getCurrentQuestion();
    
    console.log('🔍 RESPONSE DEBUG: Saving response for question:', currentQuestion?.id);
    console.log('🔍 RESPONSE DEBUG: Current question object:', currentQuestion);
    console.log('🔍 RESPONSE DEBUG: Response content:', response);
    console.log('🔍 RESPONSE DEBUG: Response content length:', response?.length);
    console.log('🔍 RESPONSE DEBUG: Current responses before saving:', responses);
    console.log('🔍 RESPONSE DEBUG: Current responses keys before saving:', Object.keys(responses || {}));
    
    // CRITICAL: Check if question ID is valid
    if (!currentQuestion?.id) {
      console.error('🚨 CRITICAL ERROR: currentQuestion.id is undefined!');
      console.error('🚨 currentQuestion:', currentQuestion);
      console.error('🚨 This will cause responses to be lost!');
      return;
    }
    
    // Save response
    const newResponses = { ...responses, [currentQuestion.id]: response };
    console.log('🔍 RESPONSE DEBUG: New responses after saving:', newResponses);
    console.log('🔍 RESPONSE DEBUG: New responses keys after saving:', Object.keys(newResponses || {}));
    console.log('🔍 RESPONSE DEBUG: Response content length:', response?.length || 0);
    
    setResponses(newResponses);
    
    // Auto-save responses to localStorage after each answer
    saveAssessmentBackup({ responses: newResponses });
    
    // Add to conversation history with question ID for editing capability
    setConversationHistory([
      ...conversationHistory,
      { type: 'question', content: currentQuestion.question, timestamp: new Date(), questionId: currentQuestion.id },
      { type: 'answer', content: response, timestamp: new Date(), questionId: currentQuestion.id }
    ]);
    
    // Analyze patterns
    if (typeof response === 'string') {
      analyzeResponsePatterns(response);
    }
    
    // Determine next action based on assessment type
    console.log(`📊 Assessment state: Question ${currentQuestionIndex + 1}/${allQuestions.length}, Phase: ${assessmentPhase}`);
    console.log(`📊 Transition check: currentQuestionIndex=${currentQuestionIndex}, initialQuestions.length=${initialQuestions.length}, assessmentPhase=${assessmentPhase}`);
    
    // Clear input immediately for all transitions
    setCurrentInput('');
    
    // Enhanced 6-phase assessment flow
    if (currentQuestionIndex === initialQuestions.length - 1 && assessmentPhase === 'opening') {
      // After initial questions (1-8), generate dynamic questions (9-15)
      console.log('🎯 Enhanced Phase 1 complete - generating dynamic questions 9-15');
      setAssessmentPhase('followup');
      await generateEnhancedDynamicQuestions(newResponses);
      return;
    } else if (assessmentPhase === 'followup' && currentQuestionIndex >= 15 - 1) {
      // After dynamic questions, generate final results
      console.log('✨ Enhanced assessment complete - generating results');
      console.log('🔍 CRITICAL DEBUG: About to call generateEnhancedInsights with newResponses:', newResponses);
      console.log('🔍 CRITICAL DEBUG: newResponses keys:', Object.keys(newResponses || {}));
      console.log('🔍 CRITICAL DEBUG: newResponses values:', Object.values(newResponses || {}));
      console.log('🔍 CRITICAL DEBUG: newResponses sample content:', Object.entries(newResponses || {}).slice(0, 3));
      setAssessmentPhase('results');
      
      // Add small delay to ensure state is fully updated, similar to testing mode
      setTimeout(async () => {
        console.log('🔍 DELAYED DEBUG: About to call generateEnhancedInsights after timeout');
        console.log('🔍 DELAYED DEBUG: newResponses keys after timeout:', Object.keys(newResponses || {}));
        await generateEnhancedInsights(newResponses);
      }, 100);
      return;
    }
    
    // Continue with next question if no phase transition
    if (currentQuestionIndex < allQuestions.length - 1) {
      console.log('➡️ Moving to next question');
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };


  // Enhanced assessment functions for 6-phase system
  const generateEnhancedDynamicQuestions = async (responses) => {
    setIsThinking(true);
    
    try {
      console.log('🚀 Generating enhanced dynamic questions 9-15...');
      
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-dynamic-questions',
          responses,
          phase: 'enhanced_dynamic',
          questionCount: 7,
          strategicContext,
          dynamicQuestionGuidance,
          initialQuestions,
          // Add data for API logging
          userId: currentUser.id, // Family member ID for logging
          studentName: currentUser.displayName || currentUser.name,
          assessmentId: null // Could add assessment ID if needed
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Generated enhanced dynamic questions:', data.questions);
        
        const enhancedQuestions = data.questions.map((q, index) => ({
          ...q,
          id: `enhanced_dynamic_${index + 1}`,
          category: 'enhanced_dynamic'
        }));
        
        // Enhanced questions are now part of allQuestions directly
        setAllQuestions([...initialQuestions, ...enhancedQuestions]);
        
        // Advance to the first dynamic question (not stay on the current one)
        const nextIndex = initialQuestions.length;
        setCurrentQuestionIndex(nextIndex);
        
        // Clear the input field since we're moving to a new question
        setCurrentInput('');
        
        console.log('🎯 Enhanced dynamic questions ready, advancing to question', nextIndex + 1);
        console.log('🎯 Current question index after dynamic generation:', nextIndex);
      } else {
        console.error('❌ Enhanced dynamic question generation failed - Status:', response.status);
        const errorText = await response.text();
        console.error('Error details:', errorText);
        
        // Fall back to generating results directly without dynamic questions
        console.log('⚠️ Falling back to direct results generation');
        setAssessmentPhase('results');
        await generateEnhancedInsights(responses);
      }
    } catch (error) {
      console.error('❌ Error generating enhanced dynamic questions:', error);
      
      // Fall back to generating results directly without dynamic questions
      console.log('⚠️ Falling back to direct results generation');
      setAssessmentPhase('results');
      await generateEnhancedInsights(responses);
    } finally {
      setIsThinking(false);
    }
  };

  // Handler for retrying processing after error
  const handleProcessingRetry = () => {
    console.log('🔄 Retrying assessment processing...');
    setProcessingError(null);
    
    // Try to recover from saved data first
    let responsesToUse = responses || {};
    
    // Check for error recovery data
    const errorData = localStorage.getItem('assessment_error');
    if (errorData) {
      try {
        const parsed = JSON.parse(errorData);
        if (parsed.responses && Object.keys(parsed.responses).length > 0) {
          console.log('📥 Recovering responses from error data');
          responsesToUse = parsed.responses;
        }
      } catch (e) {
        console.error('Failed to parse error recovery data:', e);
      }
    }
    
    // Check for backup data if no responses
    if (Object.keys(responsesToUse).length === 0) {
      const backup = recoverAssessmentBackup();
      if (backup && backup.responses) {
        console.log('📥 Recovering responses from backup');
        responsesToUse = backup.responses;
      }
    }
    
    if (Object.keys(responsesToUse).length === 0) {
      alert('Unable to recover assessment data. Please restart the assessment.');
      return;
    }
    
    generateEnhancedInsights(responsesToUse, 0);
  };

  // Generate API request preview for testing
  const generateAPIRequestPreview = () => {
    if (!mockResponseSet) return null;
    
    const mockResponses = mockResponseSet.responseSet.responses || {};
    
    const apiRequest = {
      action: 'generate-conversational-insights',
      responses: mockResponses,
      strategicContext: strategicContext || 'undefined',
      synthesisInstructions: synthesisInstructions || 'undefined',
      initialQuestions: initialQuestions || [],
      userId: currentUser?.id,
      studentName: currentUser?.displayName || currentUser?.name,
      assessmentId: null
    };
    
    return {
      url: '/api/anthropic/analysis',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: apiRequest,
      preview: {
        responsesCount: Object.keys(mockResponses).length,
        strategicContextLength: strategicContext?.length || 0,
        synthesisInstructionsLength: synthesisInstructions?.length || 0,
        initialQuestionsCount: initialQuestions?.length || 0,
        enhancedMode: true,
        strategicContextPreview: strategicContext?.substring(0, 200) + '...' || 'UNDEFINED',
        synthesisInstructionsPreview: synthesisInstructions?.substring(0, 200) + '...' || 'UNDEFINED'
      }
    };
  };

  // Save assessment data to localStorage for recovery
  const saveAssessmentBackup = (data) => {
    try {
      const backup = {
        responses: data.responses || responses,
        timestamp: new Date().toISOString(),
        userId: currentUser?.id,
        studentName: currentUser?.displayName || currentUser?.name,
        phase: assessmentPhase,
        questionIndex: currentQuestionIndex,
        allQuestions: allQuestions,
        strategicContext: strategicContext,
        synthesisInstructions: synthesisInstructions,
        parentContext: parentContext,
        studentProfile: studentProfile
      };
      localStorage.setItem('assessment_backup', JSON.stringify(backup));
      console.log('💾 Assessment backup saved to localStorage');
    } catch (error) {
      console.error('Failed to save backup:', error);
    }
  };

  // Recover assessment data from localStorage
  const recoverAssessmentBackup = () => {
    try {
      const backup = localStorage.getItem('assessment_backup');
      if (backup) {
        const data = JSON.parse(backup);
        console.log('📥 Recovered assessment backup from', data.timestamp);
        return data;
      }
    } catch (error) {
      console.error('Failed to recover backup:', error);
    }
    return null;
  };

  const generateEnhancedInsights = async (allResponses, retryCount = 0) => {
    console.log('🔍 DEBUG: generateEnhancedInsights called with allResponses:', allResponses);
    console.log('🔍 DEBUG: allResponses keys:', Object.keys(allResponses || {}));
    console.log('🔍 DEBUG: allResponses type:', typeof allResponses);
    console.log('🔍 DEBUG: allResponses values:', allResponses ? Object.values(allResponses) : 'NULL/UNDEFINED');
    console.log('🔍 DEBUG: allResponses sample entries:', allResponses ? Object.entries(allResponses).slice(0, 3) : 'NONE');
    
    // CRITICAL: Check if responses are actually empty
    if (!allResponses || Object.keys(allResponses).length === 0) {
      console.error('🚨 CRITICAL ERROR: allResponses is empty or undefined!');
      
      // Try to recover from backup
      const backup = recoverAssessmentBackup();
      if (backup && backup.responses && Object.keys(backup.responses).length > 0) {
        console.log('🔄 Attempting to recover from backup...');
        allResponses = backup.responses;
      } else {
        console.error('🚨 No backup available or backup is also empty');
        console.error('🚨 Current responses state:', responses);
        console.error('🚨 Current responses keys:', Object.keys(responses || {}));
        console.error('🚨 Assessment phase:', assessmentPhase);
        console.error('🚨 Current question index:', currentQuestionIndex);
        console.error('🚨 All questions length:', allQuestions?.length);
        
        // ABORT: Don't call API with empty responses
        alert('ERROR: No responses collected. Please try restarting the assessment.');
        setIsThinking(false);
        return;
      }
    }

    // Save backup before processing
    saveAssessmentBackup({ responses: allResponses });
    
    // Pre-generate permalink for results (even if processing fails, we have a place for it)
    const permalink = `strategic-profile-${Math.random().toString(36).substr(2, 8)}`;
    console.log('🔗 Pre-generated permalink:', permalink);
    localStorage.setItem('assessment_pending_permalink', permalink);

    setIsThinking(true);
    setProcessingError(null);
    setProcessingStatus({
      currentStep: 0,
      totalSteps: 5,
      stepName: 'Preparing your responses',
      startTime: Date.now()
    });

    // Update processing status as we progress
    const updateProcessingStep = (step, name) => {
      setProcessingStatus(prev => ({
        ...prev,
        currentStep: step,
        stepName: name
      }));
    };
    
    // Simple animation completion function for enhanced assessment
    const completeEnhancedAnimation = () => {
      return new Promise(resolve => {
        console.log('🎬 Completing enhanced assessment animation');
        // For enhanced assessment, we don't have step-by-step animation
        // so we just need a brief delay to complete the loading state
        setTimeout(() => {
          console.log('🎬 Enhanced animation complete');
          resolve();
        }, 500);
      });
    };
    
    try {
      console.log('🤖 Calling enhanced insights API...');
      
      // Step 1: Preparing data
      updateProcessingStep(1, 'Analyzing your responses');

      // Debug what we're sending to the API
      console.log('🔍 DETAILED API DEBUG: About to send request with responses:', allResponses);
      console.log('🔍 DETAILED API DEBUG: Responses object keys:', Object.keys(allResponses || {}));
      console.log('🔍 DETAILED API DEBUG: First few response values:', Object.values(allResponses || {}).slice(0, 3));
      
      const requestData = {
        action: 'generate-conversational-insights',
        responses: allResponses,
        strategicContext,
        synthesisInstructions,
        initialQuestions,
        isEnhancedAssessment: true, // CRITICAL: Tell API to use enhanced path
        // CRITICAL FIX: Include student profile and parent context data
        studentProfile,
        parentContext,
        // Add data for API logging
        userId: currentUser.id, // Family member ID for logging
        studentName: currentUser.displayName || currentUser.name,
        assessmentId: null // Could add assessment ID if needed
      };
      
      console.log('🔍 DEBUG: Request data being sent to API:', {
        responsesCount: Object.keys(allResponses).length,
        strategicContextLength: strategicContext?.length || 0,
        synthesisInstructionsLength: synthesisInstructions?.length || 0,
        initialQuestionsCount: initialQuestions?.length || 0,
        // CRITICAL DEBUG: Show student profile data is included
        hasStudentProfile: !!studentProfile,
        hasParentContext: !!parentContext,
        studentProfileCareerGoals: studentProfile?.careerGoals || 'MISSING',
        parentContextKeys: parentContext ? Object.keys(parentContext) : 'MISSING',
        synthesisInstructionsPreview: synthesisInstructions?.substring(0, 100) + '...',
        // Phase 6 formatting data
        hasFormattingInstructions: !!formattingInstructions,
        formattingInstructionsLength: formattingInstructions?.length || 0,
        hasSectionTitles: !!sectionTitles && Object.keys(sectionTitles).length > 0,
        metaphorStyle: metaphorStyle || 'DEFAULT'
      });
      
      console.log('🚨 CRITICAL DEBUG: Raw values being sent to API:');
      console.log('  strategicContext type:', typeof strategicContext);
      console.log('  strategicContext value:', strategicContext === undefined ? 'UNDEFINED' : strategicContext === null ? 'NULL' : strategicContext === '' ? 'EMPTY STRING' : `"${strategicContext.substring(0, 200)}..."`);
      console.log('  synthesisInstructions type:', typeof synthesisInstructions);
      console.log('  synthesisInstructions value:', synthesisInstructions === undefined ? 'UNDEFINED' : synthesisInstructions === null ? 'NULL' : synthesisInstructions === '' ? 'EMPTY STRING' : `"${synthesisInstructions.substring(0, 200)}..."`);
      console.log('  allResponses type:', typeof allResponses);
      console.log('  allResponses keys:', allResponses ? Object.keys(allResponses) : 'NULL/UNDEFINED');
      console.log('  allResponses length:', allResponses ? Object.keys(allResponses).length : 0);
      console.log('  isEnhancedAssessment: true (enhanced mode always enabled)');
      console.log('  Testing mode detection for API call:', {
        savedStudentId: localStorage.getItem('college-selected-student-id'),
        currentUserAuthId: currentUser?.auth_user_id || currentUser?.authUserId,
        isTestingMode: localStorage.getItem('college-selected-student-id') && localStorage.getItem('college-selected-student-id') !== (currentUser?.auth_user_id || currentUser?.authUserId)
      });
      console.log('  requestData object:', JSON.stringify(requestData, null, 2));
      
      // Check if we have synthesis instructions - if not, this is the problem!
      if (!synthesisInstructions || synthesisInstructions.length === 0) {
        console.error('🚨 CRITICAL: No synthesis instructions found! This will cause fallback results.');
        console.error('🚨 CRITICAL: Check if Phase 5 (Synthesis Instructions) was completed properly.');
        console.error('🚨 CRITICAL: Current state variables:', {
          synthesisInstructions: synthesisInstructions,
          formattingInstructions: formattingInstructions?.substring(0, 100) || 'EMPTY',
          studentSpecificConsiderations: studentSpecificConsiderations?.substring(0, 100) || 'EMPTY',
          strategicContext: strategicContext?.substring(0, 100) || 'EMPTY'
        });
      } else {
        console.log('✅ Synthesis instructions found:', {
          length: synthesisInstructions.length,
          preview: synthesisInstructions.substring(0, 200)
        });
      }
      
      // Also check formatting instructions
      if (!formattingInstructions || formattingInstructions.length === 0) {
        console.warn('⚠️ WARNING: No formatting instructions found! Using defaults.');
      } else {
        console.log('✅ Formatting instructions found:', {
          length: formattingInstructions.length,
          preview: formattingInstructions.substring(0, 200)
        });
      }
      
      // Step 2: Sending to AI
      updateProcessingStep(2, 'Identifying patterns and themes');

      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      
      console.log('🤖 Enhanced insights API response status:', response.status);
      
      if (response.ok) {
        // Step 3: Processing AI response
        updateProcessingStep(3, 'Generating strategic insights');

        const data = await response.json();
        console.log('🤖 Enhanced insights API response data:', data);
        
        // Log debug information to understand parsing issues
        if (data.debug) {
          console.log('🔍 API DEBUG INFO:', data.debug);
          console.log('🔍 Insights text preview:', data.debug.insightsPreview);
          console.log('🔍 Parse results:', data.debug.parseResult);
        }
        
        // Step 4: Formatting results
        updateProcessingStep(4, 'Crafting your unique positioning');

        // PHASE 2: Format the enhanced insights for better user experience
        let rawInsights = data.insights || data; // Store raw insights for database
        let finalInsights = rawInsights;
        console.log('🎯 ENHANCED FORMAT-INSIGHTS DEBUG START');
        console.log('🔍 Raw insights before formatting:', {
          type: typeof finalInsights,
          isString: typeof finalInsights === 'string',
          length: typeof finalInsights === 'string' ? finalInsights.length : JSON.stringify(finalInsights).length,
          preview: typeof finalInsights === 'string' ? finalInsights.substring(0, 500) : JSON.stringify(finalInsights).substring(0, 500)
        });
        
        // Helper function to create enhanced fallback insights when API fails
        const createEnhancedFallbackInsights = (rawInsights) => {
          console.log('🔄 Creating enhanced fallback insights...');
          
          // If rawInsights is already structured, return it
          if (typeof rawInsights === 'object' && rawInsights.strategicAdvantage) {
            console.log('✅ Raw insights already structured, using as-is');
            return rawInsights;
          }
          
          // If rawInsights is a string, try to parse and clean it
          if (typeof rawInsights === 'string') {
            console.log('✅ Created fallback with string insights');
            
            // Return a basic structured format
            return {
              strategicAdvantage: "Your unique combination of skills and experiences positions you well for college admissions.",
              intellectualSignature: {
                title: "How Your Brain Works",
                subtitle: "Your unique thinking style and approach",
                content: "You have a distinctive approach to learning and problem-solving that sets you apart.",
                leveragePoints: [
                  "For essays: Show your analytical process in action",
                  "For interviews: Demonstrate your systematic thinking",
                  "For school selection: Find programs that value your approach"
                ],
                strategicAngle: "Your thoughtful approach to challenges"
              },
              hiddenSuperpowers: {
                title: "Your Secret Weapons",
                subtitle: "The unique strengths that set you apart",
                content: "Your distinctive qualities make you valuable to admissions committees.",
                superpowers: ["Your unique perspective on challenges", "Your ability to connect different ideas"],
                narrative: "You bring a fresh perspective to everything you do"
              },
              strategicPositioning: {
                title: "How to Stand Out",
                subtitle: "Your competitive advantage",
                brand: "The thoughtful innovator",
                whyItWorks: "You demonstrate both depth of thinking and practical application",
                deployment: [
                  "Personal Statement: Show your problem-solving process",
                  "Essays: Use specific examples of your impact"
                ]
              },
              essayStrategy: {
                title: "Essay Game Plan",
                subtitle: "Your narrative strategy",
                goldStory: {
                  title: "Your Best Story",
                  story: "Use examples that show your thinking process in action",
                  strategicFraming: "Frame challenges as opportunities to demonstrate growth"
                },
                essayTopics: ["Your approach to solving complex problems", "Times you've made a meaningful impact"],
                theme: "The strategic thinker who creates practical solutions"
              },
              collegeRecommendations: {
                title: "College Recommendations",
                subtitle: "Schools that match your profile",
                reach: ["Top-tier schools with strong programs in your areas of interest"],
                target: ["Schools with collaborative learning environments"],
                safety: ["Schools with strong support systems and practical focus"]
              },
              actionPlan: [
                {
                  step: 1,
                  title: "Review Your Assessment Results",
                  description: "Study the insights and recommendations provided",
                  timeline: "This week"
                },
                {
                  step: 2,
                  title: "Research Target Schools",
                  description: "Look into programs that align with your interests",
                  timeline: "Next 2 weeks"
                },
                {
                  step: 3,
                  title: "Draft Essays",
                  description: "Start working on your personal statement",
                  timeline: "Month 1"
                },
                {
                  step: 4,
                  title: "Gather Recommendations",
                  description: "Reach out to teachers and mentors",
                  timeline: "Month 2"
                },
                {
                  step: 5,
                  title: "Finalize Application Materials",
                  description: "Polish all components of your applications",
                  timeline: "Month 3"
                },
                {
                  step: 6,
                  title: "Submit Applications",
                  description: "Complete and submit all college applications",
                  timeline: "Before deadlines"
                }
              ]
            };
          }
          
          // Return a very basic fallback if nothing else works
          return rawInsights;
        };

        // Enhanced format-insights call with retry mechanism
        const formatInsightsWithRetry = async (requestBody, maxRetries = 2) => {
          let lastError = null;
          let controllers = []; // Track all controllers to clean up
          
          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              console.log(`🎯 FORMAT-INSIGHTS ATTEMPT ${attempt}/${maxRetries}`);
              console.log('🎨 [1] Starting enhanced format-insights API call...');
              
              console.log('🎨 [2] Request body prepared:', {
                rawTextLength: requestBody.rawText.length,
                hasStudentConsiderations: !!requestBody.studentSpecificConsiderations,
                hasPersonalityProfile: !!requestBody.personalityProfile,
                hasStudentName: !!requestBody.studentName,
                hasSynthesisGuidance: !!requestBody.synthesisGuidance,
                synthesisGuidanceLength: requestBody.synthesisGuidance?.length || 0,
                hasFormattingInstructions: !!requestBody.formattingInstructions,
                hasTonePreferences: !!requestBody.tonePreferences,
                hasMetaphorStyle: !!requestBody.metaphorStyle,
                hasResponses: !!requestBody.responses,
                responsesCount: Object.keys(requestBody.responses || {}).length
              });
              
              console.log('🎨 [3] About to call fetch()...');
              
              // Clean up any previous controllers from failed attempts
              controllers.forEach(ctrl => {
                if (ctrl && !ctrl.signal.aborted) {
                  try {
                    ctrl.abort(new Error('Cleaning up previous attempt'));
                  } catch {
                    // Ignore cleanup errors
                  }
                }
              });
              
              // Increase timeout to 60 seconds with exponential backoff
              const timeoutDuration = 120000 + (attempt - 1) * 15000; // 120s, then 
              const controller = new AbortController();
              controllers.push(controller); // Track this controller
              
              // Check if controller is already aborted (shouldn't be, but defensive check)
              if (controller.signal.aborted) {
                console.error('🚨 AbortController created in aborted state!');
              }
              
              let timeoutId;
              try {
                timeoutId = setTimeout(() => {
                  console.warn(`⏰ Format-insights API timeout after ${timeoutDuration/1000} seconds (attempt ${attempt}), aborting request`);
                  if (!controller.signal.aborted) {
                    controller.abort(new Error(`Request timeout after ${timeoutDuration/1000} seconds`));
                  }
                }, timeoutDuration);
              } catch (timeoutError) {
                console.error('🚨 Error setting timeout:', timeoutError);
              }
              
              console.log(`🎨 [4] Calling fetch with timeout: ${timeoutDuration/1000}s`);
              
              // Debug request size
              const requestBodyString = JSON.stringify(requestBody);
              console.log(`🎨 Request body size: ${(requestBodyString.length / 1024).toFixed(2)} KB`);
              
              let formatResponse;
              try {
                formatResponse = await fetch('/api/anthropic/analysis', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: requestBodyString,
                  signal: controller.signal
                });
                
                console.log('🎨 [5] Fetch completed!');
              } catch (fetchError) {
                if (fetchError.name === 'AbortError') {
                  console.error('🚨 Fetch aborted:', fetchError.message || 'No abort reason provided');
                }
                throw fetchError;
              } finally {
                if (timeoutId) {
                  clearTimeout(timeoutId);
                }
              }
              console.log('🎨 [6] Format-insights response status:', formatResponse.status);
              
              if (formatResponse.ok) {
                console.log('🎨 [8] Response OK, parsing JSON...');
                const formatData = await formatResponse.json();
                console.log(`🎨 [9] Enhanced format-insights successful on attempt ${attempt}`);
                console.log('🎨 [10] Final insights type:', typeof formatData.results);
                return formatData.results || rawInsights;
              } else {
                const errorText = await formatResponse.text();
                console.warn(`⚠️ Enhanced format-insights failed with status ${formatResponse.status} on attempt ${attempt}:`, errorText);
                
                // If it's a server error (5xx), retry; if client error (4xx), don't retry
                if (formatResponse.status >= 500 && attempt < maxRetries) {
                  lastError = new Error(`Server error ${formatResponse.status}: ${errorText}`);
                  console.log(`🔄 Retrying in 2 seconds... (attempt ${attempt + 1}/${maxRetries})`);
                  await new Promise(resolve => setTimeout(resolve, 2000));
                  continue;
                } else {
                  throw new Error(`API error ${formatResponse.status}: ${errorText}`);
                }
              }
            } catch (formatError) {
              console.error(`🚨 Format-insights error on attempt ${attempt}:`, formatError);
              console.error('🚨 Error name:', formatError.name);
              console.error('🚨 Error message:', formatError.message);
              
              lastError = formatError;
              
              if (formatError.name === 'AbortError') {
                console.warn(`⏰ Enhanced format-insights timed out on attempt ${attempt}`);
              } else {
                console.warn(`⚠️ Enhanced format-insights error on attempt ${attempt}:`, formatError.message);
              }
              
              // If this is the last attempt or it's not a timeout/network error, don't retry
              if (attempt === maxRetries || (formatError.name !== 'AbortError' && !formatError.message?.includes('fetch'))) {
                break;
              }
              
              // Wait before retrying
              console.log(`🔄 Retrying in 2 seconds... (attempt ${attempt + 1}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, 2000));
            }
          }
          
          // All attempts failed, clean up and return formatted fallback
          console.warn('❌ All format-insights attempts failed, using enhanced fallback');
          
          // Clean up all controllers
          controllers.forEach(ctrl => {
            if (ctrl && !ctrl.signal.aborted) {
              try {
                ctrl.abort(new Error('All attempts failed - cleaning up'));
              } catch {
                // Ignore cleanup errors
              }
            }
          });
          
          throw lastError || new Error('All format-insights attempts failed');
        };
        
        // Prepare request body
        console.log('🔍 DEBUG: About to create requestBody with allResponses:', Object.keys(allResponses || {}));
        
        const requestBody = {
          action: 'format-insights',
          // Pass raw insights text directly (it's already a string from generate-insights API)
          rawText: typeof finalInsights === 'string' ? finalInsights : JSON.stringify(finalInsights),
          studentName: currentUser?.displayName || currentUser?.name,
          // Add Phase 6 formatting customizations
          formattingInstructions: formattingInstructions,
          sectionTitles: sectionTitles,
          tonePreferences: tonePreferences,
          metaphorStyle: metaphorStyle,
          // Basic student info for school recommendations
          studentProfile: studentProfile
        };
        
        try {
          finalInsights = await formatInsightsWithRetry(requestBody);
        } catch (error) {
          console.error('🚨 Format-insights completely failed, using enhanced fallback:', error);
          finalInsights = createEnhancedFallbackInsights(rawInsights);
        }
        
        console.log('🎯 ENHANCED FORMAT-INSIGHTS DEBUG END');
        
        try {
          // Save enhanced results to proper assessment responses table
          const assessmentData = {
            user_id: currentUser.id,
            assessment_type: 'enhanced',
            responses: allResponses,
            insights: rawInsights, // Store raw insights from generate-insights API
            formatted_insights: finalInsights, // Store structured insights from format-insights API
            strategic_context: strategicContext,
            synthesis_instructions: synthesisInstructions,
            initial_questions: initialQuestions,
            assessment_phase: 'completed',
            completed_at: new Date().toISOString()
          };

          // Add revision tracking if in revision mode
          if (revisionMode && originalAssessmentId) {
            assessmentData.revised_from = originalAssessmentId;
            assessmentData.revised_at = new Date().toISOString();
            console.log('💾 Saving revised assessment with revised_from:', originalAssessmentId);
          }

          const { error: saveError } = await supabase
            .from('assessment_responses')
            .insert(assessmentData);
          
          if (saveError) {
            console.error('❌ Error saving to assessment_responses:', saveError);
          } else {
            console.log('✅ Successfully saved enhanced assessment to database');
          }
        } catch (dbError) {
          console.error('❌ Database error saving enhanced assessment:', dbError);
        }
        
        // Step 5: Finalizing results
        updateProcessingStep(4, 'Preparing your personalized results');

        // Brief delay to show final step
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Complete animation before showing results
        await completeEnhancedAnimation();
        setIsThinking(false);
        setProcessingError(null);
        
        // Show results first, then offer to save responses after a delay
        setShowResults(true);
        
        // Check if in testing mode and offer to save responses after results are displayed
        const savedStudentId = localStorage.getItem('college-selected-student-id');
        const currentUserAuthId = currentUser?.auth_user_id || currentUser?.authUserId;
        const currentTestingMode = savedStudentId && savedStudentId !== currentUserAuthId;
        console.log('🔍 Testing mode check:', { currentTestingMode, savedStudentId, currentUserAuthId });
        if (currentTestingMode) {
          console.log('✅ Showing save responses modal after results animation');
          setTimeout(() => setShowSaveResponsesModal(true), 1500); // Delay until results are displayed
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Enhanced insights API failed:', response.status, errorText);
        
        setProcessingError({
          message: 'Processing failed. Please try again.',
          error: new Error(`API Error: ${response.status}`)
        });

        // Wait a moment to show error
        await new Promise(resolve => setTimeout(resolve, 2000));

        setIsThinking(false);
        
        setShowResults(true);
        
        // Check if in testing mode and offer to save responses after results are displayed
        const savedStudentId = localStorage.getItem('college-selected-student-id');
        const currentUserAuthId = currentUser?.auth_user_id || currentUser?.authUserId;
        const currentTestingMode = savedStudentId && savedStudentId !== currentUserAuthId;
        console.log('🔍 Testing mode check:', { currentTestingMode, savedStudentId, currentUserAuthId });
        if (currentTestingMode) {
          console.log('✅ Showing save responses modal after results animation');
          setTimeout(() => setShowSaveResponsesModal(true), 1500); // Delay until results are displayed
        }
      }
    } catch (error) {
      console.error('❌ Error generating enhanced insights:', error);
      
      // Auto-retry logic for specific errors
      const isRetryableError = 
        error.message?.includes('timeout') ||
        error.message?.includes('network') ||
        error.message?.includes('500') ||
        error.message?.includes('502') ||
        error.message?.includes('503') ||
        error.message?.includes('504');
      
      if (isRetryableError && retryCount < 3) {
        console.log(`🔄 Auto-retrying due to ${error.message} (attempt ${retryCount + 1}/3)...`);
        
        // Update processing status
        setProcessingStatus(prev => ({
          ...prev,
          stepName: `Retrying... (attempt ${retryCount + 2}/3)`
        }));
        
        // Wait before retry with exponential backoff
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 2000));
        
        // Retry with incremented count
        return generateEnhancedInsights(allResponses, retryCount + 1);
      }
      
      // Save error details for recovery
      const errorDetails = {
        message: error.message || 'An unexpected error occurred',
        timestamp: new Date().toISOString(),
        responses: allResponses,
        retryCount: retryCount
      };
      
      localStorage.setItem('assessment_error', JSON.stringify(errorDetails));
      
      setProcessingError({
        message: `${error.message || 'An unexpected error occurred'}. Your responses have been saved and can be recovered.`,
        error: error,
        canRecover: true
      });

      // Wait a moment to show error
      await new Promise(resolve => setTimeout(resolve, 2000));

      setIsThinking(false);
      
      // Don't automatically show results on error - let user retry
    }
  };

  const renderQuestion = () => {
    const question = getCurrentQuestion();
    
    // Debug logging
    console.log('🔍 renderQuestion debug:', {
      currentQuestionIndex,
      allQuestionsLength: allQuestions.length,
      question: question ? question.id : 'undefined',
      questionText: question ? question.question.substring(0, 50) + '...' : 'undefined',
      assessmentPhase,
      isThinking
    });
    
    // Don't render question if we're thinking (generating next phase)
    if (isThinking) {
      return null;
    }
    
    if (!question) {
      // Instead of returning null, show a fallback with debugging info
      return (
        <div className="mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-lg text-slate-900">Loading question...</p>
              <p className="text-sm text-slate-500 mt-1">
                Debug: Index {currentQuestionIndex}, Total questions: {allQuestions.length}, Phase: {assessmentPhase}
              </p>
            </div>
          </div>
          <div className="pl-11">
            <div className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50">
              <p className="text-slate-500 text-sm">Question loading... Please wait.</p>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="mb-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-slate-800 to-slate-900 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg border border-slate-700/50">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="bg-gradient-to-r from-slate-100/80 to-white/80 backdrop-blur-sm rounded-2xl p-5 border border-white/50 shadow-sm">
              <p className="text-lg text-slate-800 leading-relaxed font-medium">{question.question}</p>
            </div>
            {isThinking && (
              <div className="mt-4 p-4 bg-amber-50/80 backdrop-blur-sm border border-amber-200/50 rounded-xl">
                <div className="flex items-center gap-3 text-sm text-amber-700 animate-pulse font-medium">
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  <span>
                    {assessmentPhase === 'followup' && 'Using your strategic context to generate personalized follow-up questions...'}
                    {assessmentPhase === 'results' && 'Synthesizing insights based on your parent-created framework...'}
                    {assessmentPhase === 'opening' && 'Reviewing your responses to the strategic questions...'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {question.type === 'text' && !isThinking && (
          <div className="ml-14 mt-4">
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-1 border border-white/50 shadow-[0_8px_32px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] transition-shadow duration-200">
              <textarea
                ref={textareaRef}
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                placeholder={question.placeholder}
                className="w-full px-5 py-4 bg-transparent border-none focus:ring-0 focus:outline-none resize-none placeholder-stone-400 text-slate-800 leading-relaxed"
                rows="3"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && currentInput.trim()) {
                    e.preventDefault();
                    handleResponse(currentInput);
                  }
                }}
              />
            </div>
            <div className="mt-4 flex justify-between items-center px-1">
              <p className="text-xs text-stone-500 font-medium">Press Enter to send, Shift+Enter for new line</p>
              <button
                onClick={() => handleResponse(currentInput)}
                disabled={!currentInput.trim() || isThinking}
                className="px-6 py-2.5 bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl hover:from-slate-800 hover:to-slate-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium shadow-[0_4px_16px_rgba(15,23,42,0.2)] hover:shadow-[0_6px_20px_rgba(15,23,42,0.25)] transition-all duration-200 hover:-translate-y-0.5 disabled:transform-none"
              >
                <Send className="w-4 h-4" />
                Send
              </button>
            </div>
          </div>
        )}
        
        {question.type === 'choice' && (
          <div className="ml-14 mt-4 space-y-3">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleResponse(option)}
                disabled={isThinking}
                className="w-full text-left px-6 py-4 bg-white/70 backdrop-blur-sm border border-white/50 rounded-2xl hover:bg-white/90 hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-all duration-200 disabled:opacity-50 font-medium text-slate-800 leading-relaxed hover:-translate-y-0.5 disabled:transform-none shadow-[0_4px_20px_rgba(0,0,0,0.04)]"
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleEditAnswer = (questionId, currentAnswer) => {
    setEditingQuestionId(questionId);
    setEditingText(currentAnswer);
  };

  const saveEditedAnswer = (questionId) => {
    if (editingText.trim()) {
      // Update the response in state
      const newResponses = { ...responses, [questionId]: editingText };
      setResponses(newResponses);
      
      // Auto-save responses to localStorage after edit
      saveAssessmentBackup({ responses: newResponses });
      
      // Update conversation history
      const updatedHistory = conversationHistory.map((item) => {
        // Find the answer that corresponds to this question
        if (item.type === 'answer' && item.questionId === questionId) {
          return { ...item, content: editingText, edited: true };
        }
        return item;
      });
      setConversationHistory(updatedHistory);
      
      console.log('✏️ Updated answer for question:', questionId);
    }
    
    // Clear editing state
    setEditingQuestionId(null);
    setEditingText('');
  };

  const cancelEdit = () => {
    setEditingQuestionId(null);
    setEditingText('');
  };

  const renderConversationHistory = () => {
    return conversationHistory.map((item, index) => {
      const isEditing = item.type === 'answer' && item.questionId && editingQuestionId === item.questionId;
      
      return (
        <div key={index} className={`mb-6 ${item.type === 'answer' ? 'ml-12' : ''}`}>
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg border transition-all duration-200 ${
              item.type === 'question' 
                ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700/50 shadow-slate-900/20' 
                : 'bg-white/90 backdrop-blur-sm border-white/50 shadow-teal-500/20'
            }`}>
              {item.type === 'question' ? (
                <Bot className="w-5 h-5 text-white" />
              ) : (
                <User className="w-5 h-5 text-teal-600" />
              )}
            </div>
            <div className="flex-1">
              {item.type === 'question' ? (
                <div className="bg-gradient-to-r from-slate-100/80 to-white/80 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-sm">
                  <p className="text-sm text-slate-700 leading-relaxed font-medium">
                    {item.content}
                  </p>
                </div>
              ) : isEditing ? (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-1 border-2 border-teal-500 shadow-lg">
                  <textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    className="w-full px-5 py-3 bg-transparent border-none focus:ring-0 focus:outline-none resize-none text-slate-800"
                    rows="3"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2 px-4 pb-3">
                    <button
                      onClick={cancelEdit}
                      className="px-3 py-1 text-sm text-slate-600 hover:text-slate-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => saveEditedAnswer(item.questionId)}
                      className="px-4 py-1 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-teal-200/30 shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-all duration-200 group relative cursor-pointer hover:border-teal-300"
                  onClick={() => {
                    if (item.questionId) {
                      handleEditAnswer(item.questionId, item.content);
                    }
                  }}
                >
                  <p className="text-sm text-slate-800 leading-relaxed">
                    {item.content}
                  </p>
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs text-teal-600 bg-teal-50 px-2 py-1 rounded-full">
                      Click to edit
                    </span>
                  </div>
                  {item.edited && (
                    <span className="ml-2 text-xs text-slate-500 italic">(edited)</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  // Save responses function for testing mode
  const saveResponseSet = async () => {
    if (!responseSetName.trim()) return;
    
    try {
      const allResponses = {
        ...responses,
        followup: {} // Separate followup responses
      };
      
      // Separate followup responses from opening responses
      // Always include all responses since we're using dynamic questions
      Object.keys(responses).forEach(key => {
        allResponses.followup[key] = responses[key];
        delete allResponses[key];
      });
      
      // Use authUserId for database operations that reference auth users
      const userIdForDb = currentUser.authUserId || currentUser.id;
      
      console.log('🔍 Saving response set with user:', {
        currentUserId: currentUser.id,
        currentUserAuthUserId: currentUser.authUserId,
        userIdForDb: userIdForDb,
        currentUserName: currentUser.name,
        currentUserRole: currentUser.role
      });

      const { error } = await supabase
        .from('mock_question_responses')
        .insert({
          name: responseSetName.trim(),
          description: responseSetDescription.trim() || `Response set for ${currentUser?.name || 'testing'}`,
          responses: allResponses,
          assessment_type: 'enhanced',
          created_by: userIdForDb
        });
      
      if (error) {
        console.error('Error saving response set:', error);
        alert('Error saving response set');
      } else {
        alert('Response set saved successfully!');
        setShowSaveResponsesModal(false);
        setResponseSetName('');
        setResponseSetDescription('');
        
        // Clear testing mode flags
        localStorage.removeItem('testing-mode');
        localStorage.removeItem('mock-response-set');
      }
    } catch (error) {
      console.error('Error saving response set:', error);
      alert('Error saving response set');
    }
  };

  if (showResults) {
    return (
      <>
       
        
        <ConversationalResults navigate={navigate} />
        
        {/* Manual Save Button for Testing Mode */}
        {isTestingMode && !showSaveResponsesModal && (
          <div className="fixed bottom-8 right-8 z-40">
            <button
              onClick={() => setShowSaveResponsesModal(true)}
              className="bg-azure-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-azure-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V2" />
              </svg>
              Save Test Responses
            </button>
          </div>
        )}
        
        {/* Save Responses Modal for Testing Mode */}
        {showSaveResponsesModal && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-lg w-full p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-2">Save Response Set</h2>
              <p className="text-slate-600 text-sm mb-6">
                Would you like to save these responses as a reusable set for future tests?
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Response Set Name
                  </label>
                  <input
                    type="text"
                    value={responseSetName}
                    onChange={(e) => setResponseSetName(e.target.value)}
                    placeholder="e.g., Technical Leader Profile"
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={responseSetDescription}
                    onChange={(e) => setResponseSetDescription(e.target.value)}
                    placeholder="Brief description of this response profile"
                    rows={2}
                    className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                  />
                </div>
                
                <div className="bg-azure-50 border border-azure-200 rounded-lg p-3">
                  <p className="text-sm text-azure-800">
                    {Object.keys(responses).length} responses will be saved
                    {' (Enhanced assessment)'}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowSaveResponsesModal(false);
                    setResponseSetName('');
                    setResponseSetDescription('');
                    // Clear testing mode when skipping save
                    localStorage.removeItem('testing-mode');
                    localStorage.removeItem('mock-response-set');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Skip
                </button>
                <button
                  onClick={saveResponseSet}
                  disabled={!responseSetName.trim()}
                  className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Response Set
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Show loading state
  if (assessmentPhase === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading your profile</h2>
          <p className="text-slate-600">Gathering your academic profile and parent insights...</p>
        </div>
      </div>
    );
  }

  // Show missing data state
  if (assessmentPhase === 'missing_data') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Let's Get Your Profile Set Up</h2>
          <p className="text-slate-600 mb-6">
            To give you the most personalized insights, we need to know a bit about your academic interests and goals first. 
            This helps us ask better questions and understand what makes you unique.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/college/comprehensive-student-profile')}
              className="w-full px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium"
            >
              Complete Student Profile
            </button>
            {currentUser?.type === 'parent' && (
              <button
                onClick={() => navigate('/college/parent-observations')}
                className="w-full px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
              >
                Complete Parent Observations
              </button>
            )}
            {currentUser?.type !== 'parent' && (
              <p className="text-sm text-slate-500 text-center">
                Ask your parent to complete their observations section to get the most personalized insights.
              </p>
            )}
            <button
              onClick={() => handleBackNavigation()}
              className="w-full px-6 py-3 text-slate-600 hover:text-slate-900"
            >
              {localStorage.getItem('testing-mode') === 'true' ? 'Return to Testing Hub' : 'Return to Dashboard'}
            </button>
            
            {/* Development bypass for testing */}
            <div className="pt-4 border-t border-slate-200">
              <button
                onClick={() => {
                  console.log('🔧 Dev bypass activated - proceeding without full profile');
                  setStudentProfile({ name: 'Test Student', academicInterests: ['Test'] });
                  setAssessmentPhase('ready');
                }}
                className="w-full px-4 py-2 text-xs text-slate-400 hover:text-slate-600 border border-slate-200 rounded"
              >
                Continue Without Profile (Testing)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show ready state - assessment can begin
  if (assessmentPhase === 'ready') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-teal-50 flex items-center justify-center px-4 md:px-8 relative overflow-hidden"> {/* px-8 to px-4 for less horizontal padding */}
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-[20%] left-[10%] w-5 h-5 bg-teal-300 rounded-full animate-float-1"></div>
          <div className="absolute top-[60%] right-[15%] w-5 h-5 bg-magenta-300 rounded-full animate-float-2"></div>
          <div className="absolute bottom-[30%] left-[20%] w-5 h-5 bg-yellow-400 rounded-full animate-float-3"></div>
        </div>
        

        
        <div className="max-w-xl w-full bg-white/95 backdrop-blur-xl border border-stone-200 rounded-3xl p-6 md:p-10 text-center shadow-[0_12px_32px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.04)] relative overflow-hidden"> {/* p-20 to p-6 md:p-10, rounded-[2rem] to rounded-3xl, shadow smaller */}
          {/* Top gradient bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-magenta-500 to-yellow-500"></div>
          {/* Centered Testing Mode Banner */}
          {mockResponseSet && (
            <div className="flex flex-col items-center justify-center mb-6">
              <div className="relative mb-2">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <CheckCircle className="w-8 h-8 text-white stroke-[3] drop-shadow-sm" />
                </div>
                {/* Glow effect only around icon */}
                <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{boxShadow: '0 0 0 8px rgba(45,212,191,0.18), 0 4px 24px 0 rgba(45,212,191,0.12)'}}></div>
              </div>
              <div className="font-extrabold text-lg md:text-2xl text-slate-900 mb-1">Testing Mode: {mockResponseSet.responseSet.name}</div>
              <div className="text-base text-stone-500 font-normal">{mockResponseSet.type === '1-8' ? 'Testing mode: Using responses 1-8, will start at question 9' : 'Testing mode: Using responses 1-15, will skip to insights generation'}</div>
            </div>
          )}
          {!mockResponseSet && (
            <h1 className="text-lg md:text-2xl font-extrabold text-slate-900 mb-2 leading-tight tracking-tight">
              Ready to Begin Assessment
            </h1>
          )}
          <p className="text-base text-stone-500 mb-6 leading-relaxed max-w-[28rem] mx-auto font-normal"> {/* text-lg to text-base, mb-14 to mb-6, max-w reduced */}
            {mockResponseSet ? (
              mockResponseSet.type === '1-8' ? 
                'Testing mode: Using responses 1-8, will start at question 9' :
                'Testing mode: Using responses 1-15, will skip to insights generation'
            ) : (
              'Great! We have your profile data loaded. This personalized assessment will use your academic interests, learning style, and parent observations to ask the most relevant questions and provide better insights.'
            )}
          </p>

          {/* API Request Preview for Testing */}
          {mockResponseSet && DEBUG_VALIDATION && (() => {
            const apiPreview = generateAPIRequestPreview();
            return apiPreview ? (
              <div className="bg-slate-900/95 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-[0_8px_32px_rgba(15,23,42,0.3)] overflow-hidden mb-6">
                <div className="flex items-center justify-between p-4 bg-slate-800/50 border-b border-slate-700/30">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-violet-500/20 rounded-lg">
                      <MessageSquare className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">API Request Preview</h3>
                      <p className="text-slate-400 text-sm">Exact request that will be sent</p>
                    </div>
                  </div>
                  <div className="text-xs font-mono bg-violet-500/20 text-violet-300 px-2 py-1 rounded">
                    POST {apiPreview.url}
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  {/* Request Summary */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-slate-800/30 p-3 rounded-lg">
                      <div className="text-slate-300 font-medium mb-2">Request Data</div>
                      <div className="space-y-1 text-xs">
                        <div className="text-slate-400">Responses: <span className="text-white">{apiPreview.preview.responsesCount}</span></div>
                        <div className="text-slate-400">Enhanced Mode: <span className="text-white">{apiPreview.preview.enhancedMode ? 'Yes' : 'No'}</span></div>
                        <div className="text-slate-400">Questions: <span className="text-white">{apiPreview.preview.initialQuestionsCount}</span></div>
                      </div>
                    </div>
                    <div className="bg-slate-800/30 p-3 rounded-lg">
                      <div className="text-slate-300 font-medium mb-2">Phase Data</div>
                      <div className="space-y-1 text-xs">
                        <div className="text-slate-400">Strategic Context: <span className={`${apiPreview.preview.strategicContextLength > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {apiPreview.preview.strategicContextLength > 0 ? `${apiPreview.preview.strategicContextLength} chars` : 'UNDEFINED'}
                        </span></div>
                        <div className="text-slate-400">Synthesis Instructions: <span className={`${apiPreview.preview.synthesisInstructionsLength > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {apiPreview.preview.synthesisInstructionsLength > 0 ? `${apiPreview.preview.synthesisInstructionsLength} chars` : 'UNDEFINED'}
                        </span></div>
                      </div>
                    </div>
                  </div>

                  {/* Data Previews */}
                  <div className="space-y-3">
                    <div className="bg-slate-800/30 p-3 rounded-lg">
                      <div className="text-slate-300 font-medium mb-2 text-sm">Strategic Context Preview:</div>
                      <div className="text-xs font-mono text-slate-400 bg-slate-900/50 p-2 rounded border-l-2 border-violet-500">
                        {apiPreview.preview.strategicContextPreview}
                      </div>
                    </div>
                    <div className="bg-slate-800/30 p-3 rounded-lg">
                      <div className="text-slate-300 font-medium mb-2 text-sm">Synthesis Instructions Preview:</div>
                      <div className="text-xs font-mono text-slate-400 bg-slate-900/50 p-2 rounded border-l-2 border-violet-500">
                        {apiPreview.preview.synthesisInstructionsPreview}
                      </div>
                    </div>
                  </div>

                  {/* Full Request Body Toggle */}
                  <details className="bg-slate-800/30 rounded-lg">
                    <summary className="p-3 cursor-pointer text-slate-300 font-medium text-sm hover:text-white">
                      View Full Request Body (Click to expand)
                    </summary>
                    <div className="px-3 pb-3">
                      <pre className="text-xs font-mono text-slate-400 bg-slate-900/50 p-3 rounded overflow-x-auto">
                        {JSON.stringify(apiPreview.body, null, 2)}
                      </pre>
                    </div>
                  </details>

                  {/* Status Indicators */}
                  <div className="flex items-center gap-4 text-xs">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                      apiPreview.preview.strategicContextLength > 0 && apiPreview.preview.synthesisInstructionsLength > 0
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : 'bg-red-500/20 text-red-300 border border-red-500/30'
                    }`}>
                      {apiPreview.preview.strategicContextLength > 0 && apiPreview.preview.synthesisInstructionsLength > 0 ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          Ready for personalized insights
                        </>
                      ) : (
                        <>
                          <X className="w-3 h-3" />
                          Will use generic fallback
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null;
          })()}

          <div className="bg-stone-100 border border-stone-200 rounded-xl p-5 mb-8 text-left relative overflow-hidden"> {/* p-10 to p-5, mb-14 to mb-8 */}
            {/* Top gradient bar for info section */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-400 via-magenta-500 to-yellow-500"></div>
            <h3 className="text-base font-bold text-slate-900 mb-4 tracking-tight"> {/* text-[1.375rem] to text-base, mb-7 to mb-4 */}
              What we know about you:
            </h3>
            <div className="space-y-3"> {/* space-y-5 to space-y-3 */}
              {studentProfile?.academicInterests?.length > 0 && (
                <div className="flex items-start py-2 border-b border-stone-200"> {/* py-4 to py-2 */}
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-4 flex-shrink-0 shadow-[0_0_0_2px] shadow-teal-100"></div> {/* mr-5 to mr-4, shadow smaller */}
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-sm mb-0.5"> {/* text-[0.9375rem] to text-sm, mb-1 to mb-0.5 */}
                      Academic interests
                    </div>
                    <div className="text-stone-600 leading-relaxed text-sm"> {/* text-sm */}
                      {studentProfile.academicInterests.slice(0, 3).join(', ')}
                    </div>
                  </div>
                </div>
              )}
              {studentProfile?.careerGoals && (
                <div className="flex items-start py-2 border-b border-stone-200"> {/* py-4 to py-2 */}
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-4 flex-shrink-0 shadow-[0_0_0_2px] shadow-teal-100"></div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-sm mb-0.5">Career direction</div>
                    <div className="text-stone-600 leading-relaxed text-sm">{studentProfile.careerGoals}</div>
                  </div>
                </div>
              )}
              {studentProfile?.learningStyle && (
                <div className="flex items-start py-2 border-b border-stone-200"> {/* py-4 to py-2 */}
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-4 flex-shrink-0 shadow-[0_0_0_2px] shadow-teal-100"></div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-sm mb-0.5">Learning style</div>
                    <div className="text-stone-600 leading-relaxed text-sm">{studentProfile.learningStyle}</div>
                  </div>
                </div>
              )}
              {parentContext && (
                <div className="flex items-start py-2"> {/* py-4 to py-2 */}
                  <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-4 flex-shrink-0 shadow-[0_0_0_2px] shadow-teal-100"></div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-sm mb-0.5">Parent observations</div>
                    <div className="text-stone-600 leading-relaxed text-sm">Comprehensive insights included</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <button
            onClick={() => {
              console.log('🎯 DEBUG: Start Assessment clicked, checking mock response set...');
              const mockResponseData = localStorage.getItem('mock-response-set');
              if (mockResponseData) {
                const { responseSet, type } = JSON.parse(mockResponseData);
                console.log('🎯 DEBUG: Found mock response set on start:', { type, responseSetName: responseSet.name });
                
                if (type === '1-8') {
                  console.log('🎯 Starting at question 9 for 1-8 mode');
                  setCurrentQuestionIndex(8);
                  setAssessmentPhase('followup');
                  // Enhanced assessment mode - use dynamic questions
                  setTimeout(() => generateEnhancedDynamicQuestions(responses), 500);
                  return;
                } else if (type === '1-15') {
                  console.log('🎯 Skipping to insights for 1-15 mode');
                  setAssessmentPhase('results');
                  setTimeout(() => {
                    // Debug responseSet structure (fix: use correct variable name)
                    console.log('🔍 MOCKRESPONSE DEBUG: responseSet structure:', responseSet);
                    console.log('🔍 MOCKRESPONSE DEBUG: responseSet.responses:', responseSet?.responses);
                    console.log('🔍 MOCKRESPONSE DEBUG: responseSet.responses keys:', Object.keys(responseSet?.responses || {}));
                    
                    // Fallback: If responses are empty, try to load from responseSet
                    let responsesToUse = responses;
                    if (Object.keys(responses).length === 0) {
                      console.log('🔧 FALLBACK: responses state empty, attempting to load from responseSet');
                      
                      // Try different paths where responses might be stored
                      if (responseSet?.responses?.followup) {
                        console.log('🔧 FALLBACK: Using responseSet.responses.followup (nested structure)');
                        responsesToUse = responseSet.responses.followup;
                      } else if (responseSet?.responses) {
                        console.log('🔧 FALLBACK: Using responseSet.responses directly');
                        responsesToUse = responseSet.responses;
                      } else if (responseSet?.response_data) {
                        console.log('🔧 FALLBACK: Using responseSet.response_data');
                        responsesToUse = responseSet.response_data;
                      } else {
                        console.error('🚨 FALLBACK FAILED: No responses found in responseSet structure');
                        console.log('🔍 Available keys in responseSet:', Object.keys(responseSet || {}));
                      }
                      
                      console.log('🔧 FALLBACK: loaded responses keys:', Object.keys(responsesToUse));
                      // Also update the state for consistency
                      if (Object.keys(responsesToUse).length > 0) {
                        setResponses(responsesToUse);
                      }
                    }
                    
                    // Pass the responses directly to enhanced insights
                    console.log('🔍 DEBUG: Responses being passed to generateEnhancedInsights:', responsesToUse);
                    console.log('🔍 DEBUG: Responses keys:', Object.keys(responsesToUse));
                    console.log('🔍 DEBUG: Responses sample values:', Object.values(responsesToUse).slice(0, 3));
                    console.log('🔍 TESTING MODE DEBUG: responses state content:', responses);
                    console.log('🔍 TESTING MODE DEBUG: responses values:', Object.values(responses || {}));
                    console.log('🔍 TESTING MODE DEBUG: final responsesToUse:', responsesToUse);
                    console.log('🔍 CRITICAL DEBUG: responsesToUse structure check:', {
                      isObject: typeof responsesToUse === 'object',
                      hasKeys: Object.keys(responsesToUse).length > 0,
                      firstKey: Object.keys(responsesToUse)[0],
                      firstValue: typeof Object.values(responsesToUse)[0] === 'string' 
                        ? Object.values(responsesToUse)[0].substring(0, 100)
                        : JSON.stringify(Object.values(responsesToUse)[0]).substring(0, 100)
                    });
                    generateEnhancedInsights(responsesToUse);
                  }, 500);
                  return;
                }
              }
              console.log('🎯 DEBUG: No mock response set found, starting normal assessment');
              setAssessmentPhase('opening');
            }}
            className="px-8 py-3 bg-gradient-to-br from-slate-900 to-slate-950 text-white rounded-xl font-bold text-base cursor-pointer transition-all duration-200 min-w-[10rem] shadow-[0_4px_12px_rgba(15,23,42,0.15)] hover:shadow-[0_8px_20px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 active:translate-y-0 relative overflow-hidden group" /* px-12 py-5 to px-8 py-3, rounded-2xl to rounded-xl, text-lg to text-base, min-w reduced, shadow smaller */
          >
            <span className="relative z-10">Start Assessment</span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
          </button>
        </div>
      </div>
    );
  }

  // Phase indicators
  const phases = [
    { id: 'opening', label: 'Baseline Questions', icon: MessageCircle, count: 5 },
    { id: 'dynamic', label: 'Dynamic Questions', icon: Target, count: Math.max(0, allQuestions.length - 8) },
    { id: 'results', label: 'Insights & Results', icon: Sparkles, count: 1 }
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-teal-50 relative overflow-hidden">
      {/* Background floating orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] left-[15%] w-4 h-4 bg-teal-300/30 rounded-full animate-float-1"></div>
        <div className="absolute top-[25%] right-[20%] w-6 h-6 bg-magenta-300/20 rounded-full animate-float-2"></div>
        <div className="absolute bottom-[30%] left-[8%] w-3 h-3 bg-yellow-400/25 rounded-full animate-float-3"></div>
        <div className="absolute bottom-[15%] right-[25%] w-5 h-5 bg-teal-200/30 rounded-full animate-float-1" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Compact Header with Integrated Progress */}
        <div className="mb-6">
          {/* Navigation and time as floating elements */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => handleBackNavigation()}
              className="flex items-center gap-2 px-3 py-1.5 text-stone-600 hover:text-teal-600 bg-white/70 backdrop-blur-sm border border-stone-200/30 rounded-lg font-medium transition-all duration-200 hover:bg-white/90 hover:-translate-x-0.5 text-sm"
            >
              <ChevronLeft className="w-3 h-3" />
              {localStorage.getItem('testing-mode') === 'true' ? 'Back to Testing Hub' : 'Back to College Journey'}
            </button>
            
            <div className="flex items-center gap-1.5 text-xs text-stone-500 bg-white/50 backdrop-blur-sm px-2.5 py-1 rounded-md border border-white/40">
              <Clock className="w-3 h-3" />
              About {15 - currentQuestionIndex} min left
            </div>
          </div>
          
          {/* Streamlined Progress Bar */}
          <div className="relative bg-gradient-to-r from-white/40 to-white/60 backdrop-blur-md rounded-2xl p-4 border border-white/50 shadow-[0_2px_16px_rgba(0,0,0,0.03)]">
            {/* Progress track background */}
            <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-gradient-to-r from-stone-200 via-stone-150 to-stone-200 transform -translate-y-1/2"></div>
            
            {/* Animated progress fill */}
            <div 
              className="absolute top-1/2 left-4 h-0.5 bg-gradient-to-r from-teal-400 to-emerald-400 transform -translate-y-1/2 transition-all duration-700 ease-out"
              style={{ 
                width: `calc((100% - 2rem) * ${Math.min((phases.findIndex(p => p.id === assessmentPhase) + 1) / phases.length, 1)})` 
              }}
            ></div>
            
            <div className="relative flex items-center justify-between">
              {phases.map((phase, index) => {
                const Icon = phase.icon;
                const isActive = assessmentPhase === phase.id;
                const isCompleted = phases.findIndex(p => p.id === assessmentPhase) > index;
                
                return (
                  <div key={phase.id} className="flex flex-col items-center group">
                    {/* Icon container with elegant styling */}
                    <div className={`relative flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full transition-all duration-300 transform group-hover:scale-105 ${
                      isActive 
                        ? 'bg-gradient-to-br from-teal-400 to-teal-500 text-white shadow-lg shadow-teal-200' :
                      isCompleted 
                        ? 'bg-gradient-to-br from-emerald-400 to-emerald-500 text-white shadow-lg shadow-emerald-200' :
                      'bg-white/90 text-stone-400 border-2 border-stone-200 shadow-sm'
                    }`}>
                      {/* Subtle glow effect for active/completed states */}
                      {(isActive || isCompleted) && (
                        <div className={`absolute inset-0 rounded-full ${
                          isActive ? 'bg-teal-400' : 'bg-emerald-400'
                        } opacity-20 animate-pulse`}></div>
                      )}
                      
                      <Icon className={`w-3 h-3 md:w-4 md:h-4 relative z-10 transition-all duration-200 ${
                        isActive || isCompleted ? 'drop-shadow-sm' : ''
                      }`} />
                      
                      {/* Completion checkmark overlay */}
                      {isCompleted && !isActive && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-3 h-3 bg-white rounded-full flex items-center justify-center">
                            <svg className="w-2 h-2 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Phase label and count */}
                    <div className={`mt-1.5 text-center transition-all duration-200 ${
                      isActive ? 'text-teal-700' :
                      isCompleted ? 'text-emerald-700' :
                      'text-stone-500'
                    }`}>
                      <div className={`text-[9px] md:text-[10px] font-semibold tracking-wide transition-all duration-200 ${
                        isActive ? 'text-teal-800' : 
                        isCompleted ? 'text-emerald-800' : 
                        'text-stone-600'
                      }`}>
                        {phase.label}
                      </div>
                      
                      {/* Question count with elegant pill design */}
                      {(isActive || isCompleted) && phase.count > 0 && (
                        <div className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-medium tracking-wide transition-all duration-200 ${
                          isActive 
                            ? 'bg-teal-100/80 text-teal-700 border border-teal-200/50' :
                          isCompleted 
                            ? 'bg-emerald-100/80 text-emerald-700 border border-emerald-200/50' :
                            'bg-stone-100/80 text-stone-600 border border-stone-200/50'
                        }`}>
                          {phase.count} {phase.count === 1 ? 'step' : 'steps'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          </div>
          
          {/* Revision Mode Indicator */}
          {revisionMode && (
            <div className="mb-4">
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mx-auto max-w-2xl">
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-violet-100 rounded-full">
                    <Edit3 className="w-4 h-4 text-violet-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-violet-900 mb-1">Revision Mode</h4>
                    <p className="text-sm text-violet-700">
                      Your previous answers have been pre-populated. Edit them to add more details and get better insights.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-slate-900/95 backdrop-blur-sm rounded-2xl border border-slate-700/50 mb-6 shadow-[0_8px_32px_rgba(15,23,42,0.2)]">
              <div className="p-1.5 bg-white/10 rounded-lg">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-semibold text-white tracking-wide">
                {revisionMode ? 'Revising Your Assessment' : 
                 mockResponseSet ? `Testing: ${mockResponseSet.type} Mode` : 
                 'Conversational Assessment'}
              </span>
            </div>
            
            {/* Testing Mode Indicator */}
            {mockResponseSet && (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-azure-100/80 backdrop-blur-sm text-azure-700 rounded-xl text-xs font-semibold mb-4 border border-azure-200/50">
                <TestTube className="w-3 h-3" />
                Using {mockResponseSet.responseSet.name}
                {mockResponseSet.type === '1-8' && ' • Started at Q9'}
              </div>
            )}
            
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 leading-tight tracking-tight">
              Let's have a conversation
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
              These questions explore how you approach challenges and what genuinely interests you. Be yourself and share as much as you want.
            </p>
          </div>
        </div>

        {/* Modern Progress Bar */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white/60 backdrop-blur-sm rounded-full h-3 border border-white/30 shadow-inner">
            <div 
              className="bg-gradient-to-r from-teal-500 to-teal-600 h-full rounded-full transition-all duration-700 shadow-sm"
              style={{ width: `${((currentQuestionIndex + 1) / allQuestions.length) * 100}%` }}
            />
          </div>
          <div className="text-center mt-3 text-sm text-stone-600 font-medium">
            Question {currentQuestionIndex + 1} of {allQuestions.length}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.08),0_8px_32px_rgba(0,0,0,0.04)] border border-white/50">
            {/* Conversation History */}
            <div 
              ref={conversationContainerRef}
              className="mb-8 max-h-96 overflow-y-auto scroll-smooth"
              style={{ scrollBehavior: 'smooth' }}
            >
              <div className="space-y-1">
                {renderConversationHistory()}
              </div>
            </div>
            
            {/* Current Question */}
            {renderQuestion()}
            
          </div>
        </div>
        
        {/* Enhanced Progress Indicator */}
        <AssessmentProgressIndicator
          isProcessing={isThinking}
          currentStep={processingStatus.currentStep}
          totalSteps={processingStatus.totalSteps}
          stepName={processingStatus.stepName}
          error={processingError}
          onRetry={handleProcessingRetry}
          estimatedTime={30}
        />
      </div>
    </>
  );
};

// Use the actual results component
const AssessmentResults = () => {
  return <ConversationalResults />;
};

export default ConversationalAssessment; 